import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { usePermissions, PERMISSIONS } from '../hooks/usePermissions';
import { useClarificationTypes } from '../hooks/useMasterData';
import PermissionGuard from '../components/PermissionGuard';
import { formatDateUS, parseDateUS, formatDateTime } from '../utils/dateUtils';
import SearchableDropdown from '../components/SearchableDropdown';
import USDateInput from '../components/USDateInput';
import UserTimestamp from '../components/UserTimestamp';
import ActivityDrawer from '../components/ActivityDrawer';
import Tooltip from '../components/Tooltip';
import { MdOutlineHistory } from 'react-icons/md';
import { filterEmojis } from '../utils/emojiFilter';
import './CheckDetails.css';

const DitDrlDetails = () => {
  const { showError, showSuccess } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: clarificationTypes, loading: clarificationTypesLoading } = useClarificationTypes(true);
  // Check both id param and pathname to handle /dit-drl/new route
  const isNew = id === 'new' || window.location.pathname === '/dit-drl/new' || window.location.pathname.endsWith('/dit-drl/new');
  
  // State management
  const [ditDrl, setDitDrl] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading, will be set to false for new items
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false); // Will be set to true for new items in useEffect
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [viewingBatchId, setViewingBatchId] = useState(null);
  const [batchModalMode, setBatchModalMode] = useState('view'); // 'view', 'edit', or 'add'
  const [originalBatchData, setOriginalBatchData] = useState(null);
  const { users, getUserName, getUserById } = useUsers();
  const { can } = usePermissions();
  const [batchValidationErrors, setBatchValidationErrors] = useState({});
  const [batchSortField, setBatchSortField] = useState('batchDate');
  const [batchSortDirection, setBatchSortDirection] = useState('desc');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmBatchId, setDeleteConfirmBatchId] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveBatchId, setArchiveBatchId] = useState(null);
  const [archiveBatchIsArchived, setArchiveBatchIsArchived] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationErrorMessages] = useState([]);
  
  // Loading states for prevent multiple submissions
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [isUpdatingBatch, setIsUpdatingBatch] = useState(false);
  const [isCreatingClarification, setIsCreatingClarification] = useState(false);
  const [isUpdatingClarification, setIsUpdatingClarification] = useState(false);
  
  // Clarifications state
  const [isAddingClarification, setIsAddingClarification] = useState(false);
  const [editingClarificationId, setEditingClarificationId] = useState(null);
  const [showActivityDrawer, setShowActivityDrawer] = useState(false);
  const hasScrolledToClarification = useRef(false);
  const lastClarificationId = useRef(null);
  const hasScrolledToBatch = useRef(false);
  const lastBatchId = useRef(null);
  
  // Navigation state
  const [ditDrlIds, setDitDrlIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [navigationLoading, setNavigationLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    practiceCode: '',
    locationCode: '',
    drlReceivedDate: '',
    totalDrlAmount: 0,
    creditCardAmount: 0,
    echeckAmount: 0,
    paperCheckAmount: 0,
    interestAmount: 0,
    nonArAmount: 0,
    medicalRecordFee: 0,
    miscellaneousAmount: 0,
    assigneeId: '',
    reporteeId: '',
    drlNotes: ''
  });
  const [originalFormData, setOriginalFormData] = useState(null);
  
  // Practices and locations
  const [practices, setPractices] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [locations, setLocations] = useState([]);
  
  const [batchFormData, setBatchFormData] = useState({
    batchRunNumber: '',
    batchNumber: '',
    batchDate: '',
    batchType: 'MANUAL',
    batchAmount: '',
    batchNotes: '',
    invoiceNumber: '',
    edmNumber: '',
    batchSource: 'ONSHORE',
    correctionBatch: false
  });

  const [clarificationFormData, setClarificationFormData] = useState({
    clarificationType: '',
    details: '',
    assignee: 'ON-SHORE', // Default assignee
    reportee: 'OFF-SHORE', // Default reportee
    assigneeId: '', // Not required for default assignment
    reporterId: '', // Not required for default assignment
    status: 'OPEN',
    amount: '' // Optional amount field
  });
  const [originalClarificationData, setOriginalClarificationData] = useState(null);
  const [expandedTextareas, setExpandedTextareas] = useState({});
  const [commentAssignee, setCommentAssignee] = useState({});
  const [commentReportee, setCommentReportee] = useState({});
  const [expandedCommentsClarificationId, setExpandedCommentsClarificationId] = useState(null);
  const [commentText, setCommentText] = useState({});

  // Batch Allocation state
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationDrlBatchId, setAllocationDrlBatchId] = useState(null);
  const [allocationFilters, setAllocationFilters] = useState({
    depositDate: '',
    batchNumber: ''
  });
  const [allocationSearchError, setAllocationSearchError] = useState('');
  const [allocationSearchPerformed, setAllocationSearchPerformed] = useState(false);
  const [searchCheckBatches, setSearchCheckBatches] = useState([]);
  const [searchCheckBatchesLoading, setSearchCheckBatchesLoading] = useState(false);
  const [selectedCheckBatch, setSelectedCheckBatch] = useState(null);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [allocationAmount, setAllocationAmount] = useState('');
  
  // Deallocation confirmation state
  const [showDeallocationConfirm, setShowDeallocationConfirm] = useState(false);
  const [deallocationDrlBatchId, setDeallocationDrlBatchId] = useState(null);
  const [deallocationCheckBatchId, setDeallocationCheckBatchId] = useState(null);
  const [deallocationBatchInfo, setDeallocationBatchInfo] = useState(null);
  
  // Check batch allocations modal state
  const [showCheckBatchAllocationsModal, setShowCheckBatchAllocationsModal] = useState(false);
  const [selectedBatchForCheckAllocations, setSelectedBatchForCheckAllocations] = useState(null);
  
  // Deallocate selection modal state
  const [showDeallocateSelectionModal, setShowDeallocateSelectionModal] = useState(false);
  const [selectedBatchForDeallocation, setSelectedBatchForDeallocation] = useState(null);

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch locations when practice changes
  useEffect(() => {
    if (formData.practiceCode && practices.length > 0) {
      fetchLocations(formData.practiceCode);
    } else {
      setLocations([]);
      if (!isNew) {
        setFormData(prev => ({ ...prev, locationCode: '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.practiceCode, practices]);

  // Ref to prevent duplicate API calls
  const practicesFetchedRef = useRef(false);

  const fetchPractices = async () => {
    // Prevent duplicate calls
    if (practicesFetchedRef.current) {
      return;
    }
    
    practicesFetchedRef.current = true;
    
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
      practicesFetchedRef.current = false; // Reset on error to allow retry
    }
  };

  const fetchLocations = async (practiceCode) => {
    try {
      const practice = practices.find(p => p.code === practiceCode);
      if (practice) {
        const response = await api.getPracticeLocations(practice.practiceId);
        const locationsList = Array.isArray(response) ? response : (response?.items || []);
        setLocations(locationsList.filter(l => l.isActive !== false));
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch locations';
      showError(errorMessage);
    }
  };

  // Load DIT/DRL Payment IDs from sessionStorage
  useEffect(() => {
    if (!isNew) {
      const storedIds = sessionStorage.getItem('drlNavigationIds');
      const storedFilters = sessionStorage.getItem('drlNavigationFilters');
      
      if (storedIds) {
        try {
          const ids = JSON.parse(storedIds);
          setDitDrlIds(ids);
          const index = ids.findIndex(drlId => drlId === id);
          setCurrentIndex(index);
        } catch (err) {
          console.error('Error parsing stored DIT/DRL Payment IDs:', err);
          const errorMessage = err?.message || 'Failed to parse stored DIT/DRL Payment IDs';
          showError(errorMessage);
        }
      } else {
        // If no stored IDs, try to fetch current page
        if (storedFilters) {
          try {
            const filters = JSON.parse(storedFilters);
            const params = {
              ...filters,
              page: filters.page || 0,
              size: 50
            };
            api.searchDrlDashboard(params).then(response => {
              const drlsList = response.items || [];
              const ids = drlsList.map(d => d.drlId);
              setDitDrlIds(ids);
              const index = ids.findIndex(drlId => drlId === id);
              setCurrentIndex(index);
              // Update sessionStorage
              sessionStorage.setItem('drlNavigationIds', JSON.stringify(ids));
            }).catch(err => {
              console.error('Error fetching DRLs for navigation:', err);
              const errorMessage = err?.message || err?.data?.message || 'Failed to fetch DRLs for navigation';
              showError(errorMessage);
            });
          } catch (err) {
            console.error('Error parsing stored filters:', err);
            const errorMessage = err?.message || 'Failed to parse stored filters';
            showError(errorMessage);
          }
        }
      }
    }
  }, [id, isNew, showError]);

    // Check for tab, clarificationId, or batchId in URL and auto-switch tabs
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const clarificationIdParam = searchParams.get('clarificationId');
    const batchIdParam = searchParams.get('batchId');

    if (tabParam && ['overview', 'batches', 'clarifications'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (clarificationIdParam && id) {
      setActiveTab('clarifications');
    } else if (batchIdParam && id) {
      setActiveTab('batches');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, id]);

  // Reset scroll flags when DIT/DRL ID changes
  useEffect(() => {
    hasScrolledToClarification.current = false;
    lastClarificationId.current = null;
    hasScrolledToBatch.current = false;
    lastBatchId.current = null;
  }, [id]);

  // Expand specific clarification after clarifications are loaded
  useEffect(() => {
    const clarificationIdParam = searchParams.get('clarificationId');
    const clarifications = ditDrl?.clarifications || [];
    if (clarificationIdParam && clarifications.length > 0 && activeTab === 'clarifications') {
      const clarification = clarifications.find(c => c.clarificationId === clarificationIdParam);
      if (clarification) {
        // Only scroll if this is a new clarification ID or we haven't scrolled yet
        const isNewClarification = lastClarificationId.current !== clarificationIdParam;
        if (isNewClarification || !hasScrolledToClarification.current) {
          lastClarificationId.current = clarificationIdParam;
          hasScrolledToClarification.current = false; // Reset to allow scroll
          
          // Scroll to the clarification card after a short delay to ensure it's rendered
          setTimeout(() => {
            const element = document.querySelector(`[data-clarification-id="${clarificationIdParam}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the clarification briefly
              element.style.backgroundColor = '#fef3c7';
              setTimeout(() => {
                element.style.backgroundColor = '';
              }, 2000);
              hasScrolledToClarification.current = true;
            }
          }, 300);
        }
      }
    } else if (!clarificationIdParam) {
      // Reset when clarificationId is removed from URL
      hasScrolledToClarification.current = false;
      lastClarificationId.current = null;
    }
  }, [ditDrl?.clarifications, searchParams, activeTab]);

  // Highlight specific batch after batches are loaded
  useEffect(() => {
    const batchIdParam = searchParams.get('batchId');
    if (batchIdParam && ditDrl?.batches && ditDrl.batches.length > 0 && activeTab === 'batches') {
      const batch = ditDrl.batches.find(b => b.batchId === batchIdParam);
      if (batch) {
        // Only scroll if this is a new batch ID or we haven't scrolled yet
        const isNewBatch = lastBatchId.current !== batchIdParam;
        if (isNewBatch || !hasScrolledToBatch.current) {
          lastBatchId.current = batchIdParam;
          hasScrolledToBatch.current = false; // Reset to allow scroll

          // Scroll to the batch row after a short delay to ensure it's rendered
          setTimeout(() => {
            const element = document.querySelector(`[data-batch-id="${batchIdParam}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the batch briefly
              element.style.backgroundColor = '#fef3c7';
              setTimeout(() => {
                element.style.backgroundColor = '';
              }, 2000);
              hasScrolledToBatch.current = true;
            }
          }, 300);
        }
      }
    } else if (!batchIdParam) {
      // Reset when batchId is removed from URL
      hasScrolledToBatch.current = false;
      lastBatchId.current = null;
    }
  }, [ditDrl?.batches, searchParams, activeTab]);

  // Fetch DIT/DRL Payment details with duplicate call prevention
  const drlDetailsFetchedRef = useRef(false);
  const lastFetchedIdRef = useRef(null);
  useEffect(() => {
    // Re-check isNew based on current pathname (in case route changed)
    const currentPath = window.location.pathname;
    const isNewCheck = id === 'new' || currentPath === '/dit-drl/new' || currentPath.endsWith('/dit-drl/new');
    
    if (isNewCheck) {
      setLoading(false);
      setIsEditMode(true);
      setError(null);
      // Reset form data for new item
      const newFormData = {
        practiceCode: '',
        locationCode: '',
        drlReceivedDate: '',
        totalDrlAmount: 0,
        creditCardAmount: 0,
        echeckAmount: 0,
        paperCheckAmount: 0,
        interestAmount: 0,
        nonArAmount: 0,
        medicalRecordFee: 0,
        miscellaneousAmount: 0,
        assigneeId: '',
        reporteeId: '',
        drlNotes: ''
      };
      setFormData(newFormData);
    } else if (id && id !== 'new') {
      // Reset the ref if the ID has changed
      if (lastFetchedIdRef.current !== id) {
        drlDetailsFetchedRef.current = false;
        lastFetchedIdRef.current = id;
      }
      
      // Prevent duplicate calls for the same ID
      if (!drlDetailsFetchedRef.current) {
        drlDetailsFetchedRef.current = true;
        fetchDitDrlDetails();
      }
    } else {
      // If we're on /dit-drl/new but id is undefined, treat as new
      if (currentPath === '/dit-drl/new' || currentPath.endsWith('/dit-drl/new')) {
        setLoading(false);
        setIsEditMode(true);
        setError(null);
        setFormData({
          practiceCode: '',
          locationCode: '',
          drlReceivedDate: '',
          totalDrlAmount: 0,
          creditCardAmount: 0,
          echeckAmount: 0,
          paperCheckAmount: 0,
          interestAmount: 0,
          nonArAmount: 0,
          medicalRecordFee: 0,
          miscellaneousAmount: 0,
          assigneeId: '',
          reporteeId: '',
          drlNotes: ''
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const fetchDitDrlDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDrlById(id);
      setDitDrl(data);
      setFormData({
        practiceCode: data.practiceCode || '',
        locationCode: data.locationCode || '',
        drlReceivedDate: data.drlReceivedDate || '',
        totalDrlAmount: data.totalDrlAmount || 0,
        creditCardAmount: data.creditCardAmount || 0,
        echeckAmount: data.echeckAmount || 0,
        paperCheckAmount: data.paperCheckAmount || 0,
        interestAmount: data.interestAmount || 0,
        nonArAmount: data.nonArAmount || 0,
        medicalRecordFee: data.medicalRecordFee || 0,
        miscellaneousAmount: data.miscellaneousAmount || 0,
        assigneeId: data.assigneeId || '',
        reporteeId: data.reporteeId || '',
        drlNotes: data.drlNotes || ''
      });
    } catch (err) {
      console.error('Error fetching DIT/DRL Payment details:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load DIT/DRL Payment details. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    setFormData(prev => ({
      ...prev,
      [field]: filteredValue
    }));
  };

  const handleSave = async () => {
    try {
      // Validate required fields for new DRL
      if (isNew) {
        if (!formData.practiceCode) {
          showError('Practice is required');
          return;
        }
        if (!formData.locationCode) {
          showError('Location is required');
          return;
        }
        if (!formData.drlReceivedDate) {
          showError('DRL Received Date is required');
          return;
        }

        // For new DRL, send all data
        const dataToSave = {
          practiceCode: formData.practiceCode,
          locationCode: formData.locationCode,
          drlReceivedDate: formData.drlReceivedDate,
          totalDrlAmount: formData.totalDrlAmount === null || formData.totalDrlAmount === undefined ? 0 : parseFloat(formData.totalDrlAmount) || 0,
          creditCardAmount: formData.creditCardAmount === null || formData.creditCardAmount === undefined ? 0 : parseFloat(formData.creditCardAmount) || 0,
          echeckAmount: formData.echeckAmount === null || formData.echeckAmount === undefined ? 0 : parseFloat(formData.echeckAmount) || 0,
          paperCheckAmount: formData.paperCheckAmount === null || formData.paperCheckAmount === undefined ? 0 : parseFloat(formData.paperCheckAmount) || 0,
          interestAmount: formData.interestAmount === null || formData.interestAmount === undefined ? 0 : parseFloat(formData.interestAmount) || 0,
          nonArAmount: formData.nonArAmount === null || formData.nonArAmount === undefined ? 0 : parseFloat(formData.nonArAmount) || 0,
          medicalRecordFee: formData.medicalRecordFee === null || formData.medicalRecordFee === undefined ? 0 : parseFloat(formData.medicalRecordFee) || 0,
          miscellaneousAmount: formData.miscellaneousAmount === null || formData.miscellaneousAmount === undefined ? 0 : parseFloat(formData.miscellaneousAmount) || 0,
          checksAmount: (formData.creditCardAmount || 0) + (formData.echeckAmount || 0) + (formData.paperCheckAmount || 0),
          assigneeId: formData.assigneeId || null,
          reporteeId: formData.reporteeId || null,
          drlNotes: formData.drlNotes || null,
          otherAmount: 0,
          onshorePostingAmount: 0,
          offshorePostingAmount: 0,
          remainingAmount: formData.totalDrlAmount || 0
        };

        const newDrl = await api.createDrl(dataToSave);
        navigate(`/dit-drl/${newDrl.drlId}`);
      } else {
        // For updates, detect changes and only send modified fields
        if (!originalFormData) {
          showError('No original data to compare');
          return;
        }

        // Helper function to normalize values for comparison
        const normalizeValue = (value) => {
          if (value === null || value === undefined || value === '') return null;
          if (typeof value === 'number') return value;
          if (typeof value === 'string' && value.trim() === '') return null;
          return value;
        };

        // Detect changes
        const changes = {};
        const fieldsToCheck = [
          'creditCardAmount', 'echeckAmount', 'paperCheckAmount',
          'interestAmount', 'nonArAmount', 'medicalRecordFee', 'miscellaneousAmount',
          'assigneeId', 'reporteeId', 'drlNotes'
        ];

        let hasChanges = false;

        fieldsToCheck.forEach(field => {
          const originalVal = normalizeValue(originalFormData[field]);
          const currentVal = normalizeValue(formData[field]);
          
          // Special handling for numeric fields
          if (['creditCardAmount', 'echeckAmount', 'paperCheckAmount', 'interestAmount', 
               'nonArAmount', 'medicalRecordFee', 'miscellaneousAmount'].includes(field)) {
            const originalNum = originalVal === null ? 0 : parseFloat(originalVal) || 0;
            const currentNum = currentVal === null ? 0 : parseFloat(currentVal) || 0;
            
            if (originalNum !== currentNum) {
              changes[field] = currentNum;
              hasChanges = true;
            }
          } else {
            // For non-numeric fields
            if (originalVal !== currentVal) {
              changes[field] = currentVal;
              hasChanges = true;
            }
          }
        });

        // If no changes detected, just exit edit mode
        if (!hasChanges) {
          setIsEditMode(false);
          setOriginalFormData(null);
          showSuccess('No changes detected');
          return;
        }

        // Calculate checksAmount if any of the check amounts changed
        if (changes.creditCardAmount !== undefined || changes.echeckAmount !== undefined || changes.paperCheckAmount !== undefined) {
          const creditCard = changes.creditCardAmount !== undefined ? changes.creditCardAmount : (parseFloat(formData.creditCardAmount) || 0);
          const echeck = changes.echeckAmount !== undefined ? changes.echeckAmount : (parseFloat(formData.echeckAmount) || 0);
          const paperCheck = changes.paperCheckAmount !== undefined ? changes.paperCheckAmount : (parseFloat(formData.paperCheckAmount) || 0);
          changes.checksAmount = creditCard + echeck + paperCheck;
        }

        // Send only changed fields
        await api.updateDrl(id, changes);
        await fetchDitDrlDetails();
        setIsEditMode(false);
        setOriginalFormData(null);
        showSuccess('DRL updated successfully');
      }
    } catch (err) {
      console.error('Error saving DRL:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to save DRL. Please try again.';
      showError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    // Store original form data for change detection
    setOriginalFormData(JSON.parse(JSON.stringify(formData)));
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/dit-drl');
    } else {
      setIsEditMode(false);
      setOriginalFormData(null);
      fetchDitDrlDetails();
    }
  };

  // Batch management functions
  const handleBatchFormChange = (field, value) => {
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    setBatchFormData(prev => {
      const newData = {
        ...prev,
        [field]: filteredValue
      };
      
      // Clear edmNumber when switching to AUTO, clear batchRunNumber when switching to MANUAL
      if (field === 'batchType') {
        if (filteredValue === 'AUTO') {
          newData.edmNumber = '';
        } else if (filteredValue === 'MANUAL') {
          newData.batchRunNumber = '';
        }
      }
      
      // Clear validation errors when field changes
      if (batchValidationErrors[field]) {
        setBatchValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
      
      return newData;
    });
  };
  
  // Validate batch number uniqueness
  const validateBatchUniqueness = () => {
    const errors = {};
    const existingBatches = ditDrl?.batches || [];
    const currentBatchId = editingBatchId;
    
    // Check batch number uniqueness
    if (batchFormData.batchNumber && batchFormData.batchNumber.trim()) {
      const duplicateBatch = existingBatches.find(
        b => b.batchNumber && b.batchNumber.trim() === batchFormData.batchNumber.trim() && 
        b.batchId !== currentBatchId
      );
      if (duplicateBatch) {
        errors.batchNumber = 'Batch Number must be unique';
      }
    }
    
    setBatchValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Validate on form change
  useEffect(() => {
    if (batchFormData.batchNumber) {
      validateBatchUniqueness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchFormData.batchNumber, editingBatchId]);

  const handleAddBatch = async () => {
    // Prevent multiple submissions
    if (isCreatingBatch) return;
    
    // Validate required fields
    const errors = {};
    if (!batchFormData.batchDate || !batchFormData.batchDate.trim()) {
      errors.batchDate = 'Batch Date is required';
    }
    if (!batchFormData.batchNumber || !batchFormData.batchNumber.trim()) {
      errors.batchNumber = 'Batch Number is required';
    }
    if (!batchFormData.batchAmount || batchFormData.batchAmount === '') {
      errors.batchAmount = 'Batch Amount is required';
    } else if (isNaN(parseFloat(batchFormData.batchAmount))) {
      errors.batchAmount = 'Batch Amount must be a valid number';
    }
    
    // Validate uniqueness
    if (!validateBatchUniqueness()) {
      if (batchValidationErrors.batchNumber) {
        errors.batchNumber = batchValidationErrors.batchNumber;
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setBatchValidationErrors(errors);
      showError('Please correct the validation errors before submitting');
      return;
    }
    
    // Double check to prevent race condition
    if (isCreatingBatch) return;
    
    try {
      setIsCreatingBatch(true);
      const batchData = {
        batchRunNumber: batchFormData.batchRunNumber || '',
        batchNumber: batchFormData.batchNumber,
        invoiceNumber: batchFormData.invoiceNumber || '',
        edmNumber: batchFormData.edmNumber || '',
        batchDate: batchFormData.batchDate ? (parseDateUS(batchFormData.batchDate) || batchFormData.batchDate) : '',
        batchAmount: parseFloat(batchFormData.batchAmount) || 0,
        batchNotes: batchFormData.batchNotes || '',
        batchSource: batchFormData.batchSource || 'ONSHORE',
        batchType: batchFormData.batchType || 'MANUAL'
      };
      await api.createDrlBatch(id, batchData);
      await fetchDitDrlDetails();
      setViewingBatchId(null);
      setBatchModalMode('view');
      setBatchValidationErrors({});
      setBatchFormData({
        batchRunNumber: '',
        batchNumber: '',
        batchDate: '',
        batchType: 'MANUAL',
        batchAmount: '',
        batchNotes: '',
        invoiceNumber: '',
        edmNumber: '',
        batchSource: 'ONSHORE'
      });
    } catch (err) {
      console.error('Error creating batch:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to create batch. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatchId(batch.batchId);
    setViewingBatchId(batch.batchId);
    setBatchModalMode('edit');
    const initialBatchData = {
      batchRunNumber: batch.batchRunNumber || '',
      batchNumber: batch.batchNumber || '',
      batchDate: batch.batchDate || '',
      batchType: batch.batchType || 'MANUAL',
      batchAmount: batch.batchAmount || '',
      batchNotes: batch.batchNotes || '',
      invoiceNumber: batch.invoiceNumber || '',
      edmNumber: batch.edmNumber || '',
      batchSource: batch.batchSource || 'ONSHORE',
      correctionBatch: batch.isCorrectionBatch || false
    };
    setBatchFormData(initialBatchData);
    setOriginalBatchData(JSON.parse(JSON.stringify(initialBatchData)));
    setBatchValidationErrors({});
  };

  const handleUpdateBatch = async () => {
    // Validate required fields
    const errors = {};
    if (!batchFormData.batchDate || !batchFormData.batchDate.trim()) {
      errors.batchDate = 'Batch Date is required';
    }
    if (!batchFormData.batchNumber || !batchFormData.batchNumber.trim()) {
      errors.batchNumber = 'Batch Number is required';
    }
    if (!batchFormData.batchAmount || batchFormData.batchAmount === '') {
      errors.batchAmount = 'Batch Amount is required';
    } else if (isNaN(parseFloat(batchFormData.batchAmount))) {
      errors.batchAmount = 'Batch Amount must be a valid number';
    }
    
    // Validate uniqueness
    if (!validateBatchUniqueness()) {
      if (batchValidationErrors.batchNumber) {
        errors.batchNumber = batchValidationErrors.batchNumber;
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setBatchValidationErrors(errors);
      showError('Please correct the validation errors before submitting');
      return;
    }
    
    try {
      const batchData = {
        batchRunNumber: batchFormData.batchRunNumber || '',
        batchNumber: batchFormData.batchNumber,
        invoiceNumber: batchFormData.invoiceNumber || '',
        edmNumber: batchFormData.edmNumber || '',
        batchDate: batchFormData.batchDate ? (parseDateUS(batchFormData.batchDate) || batchFormData.batchDate) : '',
        batchAmount: parseFloat(batchFormData.batchAmount) || 0,
        batchNotes: batchFormData.batchNotes || '',
        batchSource: batchFormData.batchSource || 'ONSHORE',
        batchType: batchFormData.batchType || 'MANUAL',
        archived: batchFormData.archived || false
      };
      await api.updateDrlBatch(id, editingBatchId, batchData);
      await fetchDitDrlDetails();
      setEditingBatchId(null);
      setViewingBatchId(null);
      setBatchModalMode('view');
      setOriginalBatchData(null);
      setBatchValidationErrors({});
      setBatchFormData({
        batchRunNumber: '',
        batchNumber: '',
        batchDate: '',
        batchType: 'MANUAL',
        batchAmount: '',
        batchNotes: '',
        invoiceNumber: '',
        edmNumber: '',
        batchSource: 'ONSHORE'
      });
    } catch (err) {
      console.error('Error updating batch:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to update batch. Please try again.';
      showError(errorMessage);
    } finally {
      setIsUpdatingBatch(false);
    }
  };

  const handleArchiveBatchClick = (batchId, isArchived) => {
    setArchiveBatchId(batchId);
    setArchiveBatchIsArchived(isArchived);
    setShowArchiveConfirm(true);
  };
  
  const handleArchiveBatchConfirm = async () => {
    if (!archiveBatchId) return;
    
    // Check permission
    if (!can(PERMISSIONS.DIT_DRL_BATCH_UPDATE)) {
      alert('You do not have permission to archive/unarchive batches.');
      setShowArchiveConfirm(false);
      setArchiveBatchId(null);
      setArchiveBatchIsArchived(false);
      return;
    }
    
    try {
      await api.archiveDrlBatch(id, archiveBatchId, !archiveBatchIsArchived);
      await fetchDitDrlDetails();
      setShowArchiveConfirm(false);
      setArchiveBatchId(null);
      setArchiveBatchIsArchived(false);
    } catch (err) {
      console.error('Error archiving batch:', err);
      const errorMessage = err?.data?.message || err?.message || `Failed to ${archiveBatchIsArchived ? 'unarchive' : 'archive'} batch. Please try again.`;
      showError(errorMessage);
    }
  };
  
  const handleArchiveBatchCancel = () => {
    setShowArchiveConfirm(false);
    setArchiveBatchId(null);
    setArchiveBatchIsArchived(false);
  };

  const handleDeleteBatch = async (batchId, isArchived) => {
    if (!can(PERMISSIONS.DIT_DRL_BATCH_DELETE)) {
      alert('You do not have permission to delete batches.');
      return;
    }
    
    // Check if batch is archived
    if (!isArchived) {
      alert('Only archived batches can be deleted. Please archive the batch first before deleting.');
      return;
    }
    
    // Show delete confirmation modal
    setDeleteConfirmBatchId(batchId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmBatchId) return;
    
    try {
      await api.deleteDrlBatch(id, deleteConfirmBatchId);
      await fetchDitDrlDetails();
      setShowDeleteConfirm(false);
      setDeleteConfirmBatchId(null);
    } catch (err) {
      console.error('Error deleting batch:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to delete batch. Please try again.';
      showError(errorMessage);
      setShowDeleteConfirm(false);
      setDeleteConfirmBatchId(null);
    }
  };

  const handleDeleteConfirmCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmBatchId(null);
  };

  const handleCancelBatch = () => {
    // Restore original batch data if editing (not adding)
    if (editingBatchId && originalBatchData) {
      setBatchFormData(JSON.parse(JSON.stringify(originalBatchData)));
    } else {
      // Reset to default if adding new batch
      setBatchFormData({
        batchRunNumber: '',
        batchNumber: '',
        batchDate: '',
        batchType: 'MANUAL',
        batchAmount: '',
        batchNotes: '',
        invoiceNumber: '',
        edmNumber: '',
        batchSource: 'ONSHORE'
      });
    }
    setEditingBatchId(null);
    setOriginalBatchData(null);
    setBatchValidationErrors({});
    // Close the modal by resetting viewing state
    setViewingBatchId(null);
    setBatchModalMode('view');
  };

  // Clarification handlers
  const handleClarificationFormChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    
    // Auto-select OFF-SHORE in reporter when ON-SHORE is selected for assignee
    if (field === 'assignee' && value) {
      setClarificationFormData(prev => ({
        ...prev,
        [field]: filteredValue,
        reportee: filteredValue === 'ON-SHORE' ? 'OFF-SHORE' : prev.reportee // Auto-select OFF-SHORE if ON-SHORE selected
      }));
    } else {
      setClarificationFormData(prev => ({
        ...prev,
        [field]: filteredValue
      }));
    }
  };

  const toggleCommentsExpanded = (clarificationId) => {
    setExpandedCommentsClarificationId(prev => 
      prev === clarificationId ? null : clarificationId
    );
  };

  const handleAddClarification = async () => {
    // Prevent multiple submissions
    if (isCreatingClarification) return;
    
    // Validate required fields - clarification type is required
    if (!clarificationFormData.clarificationType || !clarificationFormData.clarificationType.trim()) {
      showError('Clarification Type is required');
      return;
    }
    
    try {
      setIsCreatingClarification(true);
      // Convert amount to number (0 if empty)
      const formDataToSend = {
        ...clarificationFormData,
        amount: clarificationFormData.amount && clarificationFormData.amount !== '' 
          ? parseFloat(clarificationFormData.amount) || 0 
          : 0
      };
      await api.createDrlClarification(id, formDataToSend);
      await fetchDitDrlDetails(); // Refresh DIT/DRL data to get updated clarifications
      setIsAddingClarification(false);
      setClarificationFormData({
        clarificationType: '',
        details: '',
        assignee: 'ON-SHORE', // Default assignee
        reportee: 'OFF-SHORE', // Default reportee
        assigneeId: '', // Not required for default assignment
        reporterId: '', // Not required for default assignment
        status: 'OPEN',
        amount: '' // Optional amount field
      });
    } catch (err) {
      console.error('Error creating clarification:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to create clarification. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCreatingClarification(false);
    }
  };

  const handleEditClarification = (clarification) => {
    setEditingClarificationId(clarification.clarificationId);
    // Automatically expand comments when editing
    setExpandedCommentsClarificationId(clarification.clarificationId);
    const initialClarificationData = {
      clarificationType: clarification.clarificationType || '',
      details: clarification.details || '',
      assignee: clarification.assignee || 'ON-SHORE', // Default to ON-SHORE if not set
      reportee: clarification.reportee || 'OFF-SHORE', // Default to OFF-SHORE if not set
      assigneeId: clarification.assigneeId || '',
      reporterId: clarification.reporterId || '',
      status: clarification.status || 'OPEN'
    };
    setClarificationFormData(initialClarificationData);
    // Store original clarification data for change detection
    setOriginalClarificationData(JSON.parse(JSON.stringify(initialClarificationData)));
  };

  const handleUpdateClarification = async () => {
    // Prevent multiple submissions
    if (isUpdatingClarification) return;
    
    try {
      // Check if data has changed
      if (originalClarificationData) {
        const hasChanges = Object.keys(clarificationFormData).some(key => {
          const currentValue = clarificationFormData[key];
          const originalValue = originalClarificationData[key];
          
          // Handle string fields - compare trimmed strings
          const currentStr = String(currentValue || '').trim();
          const originalStr = String(originalValue || '').trim();
          return currentStr !== originalStr;
        });
        
        if (!hasChanges) {
          // No changes, just exit edit mode without API call
          const previousClarificationId = editingClarificationId;
          setEditingClarificationId(null);
          setOriginalClarificationData(null);
          // Keep comments expanded after update
          setExpandedCommentsClarificationId(previousClarificationId);
          setClarificationFormData({
            clarificationType: '',
            details: '',
            assignee: 'ON-SHORE', // Default assignee
            reportee: 'OFF-SHORE', // Default reportee
            assigneeId: '', // Not required for default assignment
            reporterId: '', // Not required for default assignment
            status: 'OPEN',
            amount: '' // Optional amount field
          });
          return;
        }
      }
      
      // Validate required fields - clarification type is required
      if (!clarificationFormData.clarificationType || !clarificationFormData.clarificationType.trim()) {
        showError('Clarification Type is required');
        return;
      }
      
      setIsUpdatingClarification(true);
      // Convert amount to number (0 if empty)
      const formDataToSend = {
        ...clarificationFormData,
        amount: clarificationFormData.amount && clarificationFormData.amount !== '' 
          ? parseFloat(clarificationFormData.amount) || 0 
          : 0
      };
      await api.updateDrlClarification(id, editingClarificationId, formDataToSend);
      await fetchDitDrlDetails(); // Refresh DIT/DRL data to get updated clarifications
      const previousClarificationId = editingClarificationId;
      setEditingClarificationId(null);
      setOriginalClarificationData(null);
      // Keep comments expanded after update
      setExpandedCommentsClarificationId(previousClarificationId);
      setClarificationFormData({
        clarificationType: '',
        details: '',
        assignee: 'ON-SHORE', // Default assignee
        reportee: 'OFF-SHORE', // Default reportee
        assigneeId: '', // Not required for default assignment
        reporterId: '', // Not required for default assignment
        status: 'OPEN',
        amount: '' // Optional amount field
      });
    } catch (err) {
      console.error('Error updating clarification:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to update clarification. Please try again.';
      showError(errorMessage);
    } finally {
      setIsUpdatingClarification(false);
    }
  };

  const handleCancelClarification = () => {
    setIsAddingClarification(false);
    // Restore original clarification data if editing (not adding)
    if (editingClarificationId && originalClarificationData) {
      setClarificationFormData(JSON.parse(JSON.stringify(originalClarificationData)));
      // Keep comments expanded when canceling edit
    } else {
      // Reset to default if adding new clarification
      // Collapse comments when canceling add
      setExpandedCommentsClarificationId(null);
      setClarificationFormData({
        clarificationType: '',
        details: '',
        assignee: 'ON-SHORE', // Default assignee
        reportee: 'OFF-SHORE', // Default reportee
        assigneeId: '', // Not required for default assignment
        reporterId: '', // Not required for default assignment
        status: 'OPEN',
        amount: '' // Optional amount field
      });
    }
    setEditingClarificationId(null);
    setOriginalClarificationData(null);
  };

  const handleAddComment = async (clarificationId) => {
    const comment = commentText[clarificationId];
    const assignee = commentAssignee[clarificationId];
    let reportee = commentReportee[clarificationId];
    
    // Auto-set reporter based on assignee if assignee is selected
    if (assignee && !reportee) {
      reportee = assignee === 'ON-SHORE' ? 'OFF-SHORE' : 'ON-SHORE';
      // Update state to reflect the auto-set reporter
      setCommentReportee(prev => ({
        ...prev,
        [clarificationId]: reportee
      }));
    }
    
    // At least comment or assignee must be provided
    if ((!comment || !comment.trim()) && !assignee) {
      showError('Please enter a comment or select an assignee');
      return;
    }

    try {
      const commentData = {};
      if (comment && comment.trim()) {
        commentData.newComment = comment.trim();
      }
      if (assignee) {
        commentData.assignee = assignee;
        // Always set reporter when assignee is set
        if (reportee) {
          commentData.reportee = reportee;
        }
      }
      
      await api.addDrlClarificationComment(id, clarificationId, commentData);
      await fetchDitDrlDetails(); // Refresh DIT/DRL data to get updated clarifications
      
      // Clear comment form
      setCommentText(prev => ({
        ...prev,
        [clarificationId]: ''
      }));
      setCommentAssignee(prev => {
        const updated = { ...prev };
        delete updated[clarificationId];
        return updated;
      });
      setCommentReportee(prev => {
        const updated = { ...prev };
        delete updated[clarificationId];
        return updated;
      });
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to add comment. Please try again.';
      showError(errorMessage);
    }
  };

  const handleSortBatches = (field) => {
    if (batchSortField === field) {
      setBatchSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setBatchSortField(field);
      setBatchSortDirection('asc');
    }
  };
  
  const getSortArrow = (field) => {
    if (batchSortField !== field) return null;
    return <span className="sort-arrow">{batchSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };
  
  // Get sorted batches
  const getSortedBatches = () => {
    if (!ditDrl?.batches || ditDrl.batches.length === 0) return [];
    
    const batches = [...ditDrl.batches];
    return batches.sort((a, b) => {
      let aVal = a[batchSortField];
      let bVal = b[batchSortField];
      
      if (batchSortField === 'batchDate' || batchSortField === 'createdAt' || batchSortField === 'updatedAt') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (batchSortField === 'batchAmount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else {
        aVal = aVal || '';
        bVal = bVal || '';
      }
      
      if (batchSortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Calculate TAT (Turnaround Time) in days between two dates
  const calculateTAT = (endDate, startDate) => {
    if (!endDate || !startDate) return null;
    
    try {
      // Handle ISO datetime strings (extract date part if needed)
      const endDateStr = endDate.includes('T') ? endDate.split('T')[0] : endDate;
      const startDateStr = startDate.includes('T') ? startDate.split('T')[0] : startDate;
      
      const end = new Date(endDateStr);
      const start = new Date(startDateStr);
      
      // Check if dates are valid
      if (isNaN(end.getTime()) || isNaN(start.getTime())) {
        return null;
      }
      
      // Calculate difference in milliseconds and convert to days
      const diffTime = end - start;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error('Error calculating TAT:', error);
      return null;
    }
  };

  // Format TAT value for display
  const formatTAT = (days) => {
    if (days === null || days === undefined) return 'N/A';
    return `${days} day${days === 1 ? '' : 's'}`;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'COMPLETED': 'status-complete',
      'UNDER_CLARIFICATIONS': 'status-clarification',
      'IN_PROGRESS': 'status-progress',
      'NOT_STARTED': 'status-not-started',
      'OVER_POSTED': 'status-over-posted'
    };
    return statusMap[status] || 'status-not-started';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      'UNDER_CLARIFICATIONS': 'Under Clarifications',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
      'OVER_POSTED': 'Over Posted'
    };
    return statusMap[status] || status;
  };

  const getReconciliationStatusClass = (reconciliationStatus) => {
    const statusMap = {
      'RECONCILED': 'status-complete',
      'OVER_RECONCILED': 'status-over-posted',
      'UNRECONCILED': 'status-pending'
    };
    return statusMap[reconciliationStatus] || 'status-pending';
  };

  const formatReconciliationStatus = (reconciliationStatus) => {
    const statusMap = {
      'RECONCILED': 'Reconciled',
      'OVER_RECONCILED': 'Excess Reconciled',
      'UNRECONCILED': 'Pending Reconciliation'
    };
    return statusMap[reconciliationStatus] || reconciliationStatus;
  };

  // Helper function to truncate text and show tooltip
  const TruncatedText = ({ text, maxLength = 15 }) => {
    if (!text || text === 'N/A') return <span>{text || 'N/A'}</span>;
    
    const textStr = String(text);
    const shouldTruncate = textStr.length > maxLength;
    const displayText = shouldTruncate ? textStr.substring(0, maxLength) + '...' : textStr;
    
    if (shouldTruncate) {
      return (
        <Tooltip text={textStr} position="top">
          <span className="batch-truncated-text">{displayText}</span>
        </Tooltip>
      );
    }
    
    return <span>{displayText}</span>;
  };

  // Navigation functions
  const handlePreviousDrl = async () => {
    if (currentIndex > 0 && ditDrlIds.length > 0) {
      const previousId = ditDrlIds[currentIndex - 1];
      setNavigationLoading(true);
      navigate(`/dit-drl/${previousId}`);
    } else if (currentIndex === 0 && ditDrlIds.length > 0) {
      // Try to fetch previous page
      const storedFilters = sessionStorage.getItem('drlNavigationFilters');
      if (storedFilters) {
        try {
          const filters = JSON.parse(storedFilters);
          const currentPage = filters.page || 0;
          if (currentPage > 0) {
            // Fetch previous page
            const params = {
              ...filters,
              page: currentPage - 1,
              size: 50
            };
            setNavigationLoading(true);
            const response = await api.searchDrlDashboard(params);
            const drlsList = response.items || [];
            if (drlsList.length > 0) {
              const lastDrlId = drlsList[drlsList.length - 1].drlId;
              // Update sessionStorage - prepend previous page DRLs
              const allIds = [...drlsList.map(d => d.drlId), ...ditDrlIds];
              sessionStorage.setItem('drlNavigationIds', JSON.stringify(allIds));
              sessionStorage.setItem('drlNavigationFilters', JSON.stringify({ ...filters, page: currentPage - 1 }));
              navigate(`/dit-drl/${lastDrlId}`);
            } else {
              setNavigationLoading(false);
            }
          } else {
            setNavigationLoading(false);
          }
        } catch (err) {
          console.error('Error fetching previous page:', err);
          const errorMessage = err?.message || err?.data?.message || 'Failed to fetch previous page';
          showError(errorMessage);
          setNavigationLoading(false);
        }
      } else {
        setNavigationLoading(false);
      }
    } else {
      setNavigationLoading(false);
    }
  };

  const handleNextDrl = async () => {
    if (currentIndex >= 0 && currentIndex < ditDrlIds.length - 1) {
      const nextId = ditDrlIds[currentIndex + 1];
      setNavigationLoading(true);
      navigate(`/dit-drl/${nextId}`);
    } else if (currentIndex === ditDrlIds.length - 1 && ditDrlIds.length > 0) {
      // Try to fetch next page
      const storedFilters = sessionStorage.getItem('drlNavigationFilters');
      if (storedFilters) {
        try {
          const filters = JSON.parse(storedFilters);
          const currentPage = filters.page || 0;
          const params = {
            ...filters,
            page: currentPage + 1,
            size: 50
          };
          setNavigationLoading(true);
          const response = await api.searchDrlDashboard(params);
          const drlsList = response.items || [];
          if (drlsList.length > 0) {
            const firstDrlId = drlsList[0].drlId;
            // Update sessionStorage - append next page DRLs
            const allIds = [...ditDrlIds, ...drlsList.map(d => d.drlId)];
            sessionStorage.setItem('drlNavigationIds', JSON.stringify(allIds));
            sessionStorage.setItem('drlNavigationFilters', JSON.stringify({ ...filters, page: currentPage + 1 }));
            navigate(`/dit-drl/${firstDrlId}`);
          } else {
            setNavigationLoading(false);
          }
        } catch (err) {
          console.error('Error fetching next page:', err);
          const errorMessage = err?.message || err?.data?.message || 'Failed to fetch next page';
          showError(errorMessage);
          setNavigationLoading(false);
        }
      } else {
        setNavigationLoading(false);
      }
    } else {
      setNavigationLoading(false);
    }
  };

  // Reset navigation loading when DRL ID changes
  useEffect(() => {
    setNavigationLoading(false);
  }, [id]);

  // Allocation functions
  const handleAllocateClick = (batchId) => {
    setAllocationDrlBatchId(batchId);
    setAllocationFilters({
      depositDate: '',
      batchNumber: ''
    });
    setAllocationSearchError('');
    setAllocationSearchPerformed(false);
    setSearchCheckBatches([]);
    setSelectedCheckBatch(null);
    setSelectedCheckId(null);
    setAllocationAmount('');
    setShowAllocationModal(true);
  };

  const handleDeallocateClick = (batchId, checkBatchId, allocationInfo = null) => {
    // Find the batch and allocation info for display
    const batch = ditDrl?.batches?.find(b => b.batchId === batchId);
    const allocation = batch?.allocations?.find(a => a.checkBatchId === checkBatchId);
    
    setDeallocationDrlBatchId(batchId);
    setDeallocationCheckBatchId(checkBatchId);
    setDeallocationBatchInfo({
      checkBatchNumber: allocation?.checkBatchNumber || 'N/A',
      checkNumber: allocation?.checkNumber || 'N/A',
      allocatedAmount: allocation?.allocatedAmount || 0
    });
    setShowDeallocationConfirm(true);
  };

  const handleDeallocationConfirm = async () => {
    if (!deallocationDrlBatchId || !deallocationCheckBatchId) {
      return;
    }
    
    try {
      await api.deallocateCheckBatchFromDrlBatch(id, deallocationDrlBatchId, deallocationCheckBatchId);
      showSuccess('Check batch deallocated successfully');
      setShowDeallocationConfirm(false);
      setDeallocationDrlBatchId(null);
      setDeallocationCheckBatchId(null);
      setDeallocationBatchInfo(null);
      fetchDitDrlDetails();
    } catch (error) {
      console.error('Error deallocating check batch:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to deallocate check batch';
      showError(errorMessage);
    }
  };

  const handleDeallocationCancel = () => {
    setShowDeallocationConfirm(false);
    setDeallocationDrlBatchId(null);
    setDeallocationCheckBatchId(null);
    setDeallocationBatchInfo(null);
  };

  const handleSearchCheckBatches = async () => {
    // Clear previous error
    setAllocationSearchError('');
    
    const hasDate = allocationFilters.depositDate && allocationFilters.depositDate.trim() !== '';
    const hasBatchNumber = allocationFilters.batchNumber && allocationFilters.batchNumber.trim() !== '';
    
    // Validate at least one search criteria is provided
    if (!hasDate && !hasBatchNumber) {
      setAllocationSearchError('Please enter a deposit date and/or batch number to search');
      return;
    }

    // USDateInput stores completed dates as YYYY-MM-DD while displaying MM/DD/YYYY.
    if (hasDate) {
      const dateValue = allocationFilters.depositDate.trim();
      const datePattern = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
      
      if (!datePattern.test(dateValue)) {
        setAllocationSearchError('Invalid date format. Please use MM/DD/YYYY format (e.g., 12/31/2024)');
        return;
      }
      
      // Additional validation: check if date is valid (not like 02/31/2024)
      try {
        const [year, month, day] = dateValue.split('-').map(Number);
        const testDate = new Date(year, month - 1, day);
        
        if (testDate.getMonth() !== month - 1 || testDate.getDate() !== day || testDate.getFullYear() !== year) {
          setAllocationSearchError('Invalid date. Please enter a valid date in MM/DD/YYYY format');
          return;
        }
      } catch (error) {
        setAllocationSearchError('Invalid date. Please enter a valid date in MM/DD/YYYY format');
        return;
      }
    }

    try {
      setSearchCheckBatchesLoading(true);
      const params = {
        practiceCode: ditDrl?.practiceCode || undefined,
        locationCode: ditDrl?.locationCode || undefined,
        page: 0,
        size: 200
      };
      if (hasDate) params.depositDate = allocationFilters.depositDate.trim();
      if (hasBatchNumber) params.batchNumber = allocationFilters.batchNumber.trim();
      const response = await api.searchCheckBatches(params);

      const results = response.items || [];
      setSearchCheckBatches(results);
      setAllocationSearchPerformed(true);
      setAllocationSearchError('');
      
      // Show notification if no results found
      if (results.length === 0) {
        showError('No check batches found matching your search criteria');
      }
    } catch (error) {
      console.error('Error searching check batches:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to search check batches';
      setAllocationSearchError(errorMessage);
      setSearchCheckBatches([]);
      setAllocationSearchPerformed(true);
    } finally {
      setSearchCheckBatchesLoading(false);
    }
  };

  const handleAllocateSubmit = async () => {
    if (!selectedCheckId || !selectedCheckBatch || !allocationAmount) {
      showError('Please select a check batch and enter an amount');
      return;
    }
    
    const amount = parseFloat(allocationAmount);
    if (isNaN(amount) || amount <= 0) {
      showError('Please enter a valid allocation amount');
      return;
    }
    
    try {
      await api.allocateCheckBatchToDrlBatch(id, allocationDrlBatchId, {
        checkId: selectedCheckId,
        checkBatchId: selectedCheckBatch.batchId,
        amount: amount
      });
      
      showSuccess('Check batch allocated successfully');
      setShowAllocationModal(false);
      setAllocationFilters({
        depositDate: '',
        batchNumber: ''
      });
      setAllocationSearchError('');
      setAllocationSearchPerformed(false);
      setSearchCheckBatches([]);
      setSelectedCheckBatch(null);
      setSelectedCheckId(null);
      setAllocationAmount('');
      fetchDitDrlDetails();
    } catch (error) {
      console.error('Error allocating check batch:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to allocate check batch';
      showError(errorMessage);
    }
  };

  const hasAllocations = (batch) => {
    return batch.allocations && batch.allocations.length > 0;
  };

  const getAllocationCount = (batch) => {
    return batch.allocations ? batch.allocations.length : 0;
  };
  
  const getTotalAllocatedAmount = (batch) => {
    if (!batch.allocations || batch.allocations.length === 0) {
      return 0;
    }
    return batch.allocations.reduce((sum, allocation) => {
      return sum + (allocation.allocatedAmount || 0);
    }, 0);
  };

  // Re-check isNew based on current pathname
  const currentPath = window.location.pathname;
  const isNewCheck = isNew || currentPath === '/dit-drl/new' || currentPath.endsWith('/dit-drl/new');

  // Navigation loading overlay
  if (navigationLoading) {
    return (
      <div className={`check-details-page ${showActivityDrawer ? 'activity-sidebar-open' : ''}`}>
        <div className="navigation-loading-overlay">
          <div className="navigation-loading-content">
            <div className="navigation-loading-spinner"></div>
            <p className="navigation-loading-text">Loading DRL details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="check-details-page">
        <div className="skeleton-loader">
          <div className="skeleton-header"></div>
          <div className="skeleton-card">
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || (!isNewCheck && !ditDrl)) {
    return (
      <div className="check-details-page">
        <div className="error-state">
          <p>{error || 'DRL not found'}</p>
          <button className="btn-primary" onClick={() => navigate('/dit-drl')}>
            Back to DRL
          </button>
        </div>
      </div>
    );
  }

  const displayData = isNewCheck ? formData : ditDrl;

  return (
    <div className={`check-details-page ${showActivityDrawer ? 'activity-sidebar-open' : ''}`} data-testid="dit-drl-details-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-top-row">
            <button className="back-btn" onClick={() => navigate('/dit-drl')}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>
            <div className="title-row">
              <h1 className="page-title">
                {isNewCheck ? 'New DRL' : (displayData?.drlNumber || 'DRL Details')}
              </h1>
              {!isNewCheck && displayData?.status && (
                <span className={`status-badge-large ${getStatusClass(displayData.status)}`}>
                  {formatStatus(displayData.status)}
                </span>
              )}
              {!isNewCheck && displayData?.reconciliationStatus && (
                <span className={`status-badge-large ${getReconciliationStatusClass(displayData.reconciliationStatus)}`}>
                  {formatReconciliationStatus(displayData.reconciliationStatus)}
                </span>
              )}
            </div>
          </div>
          {!isNewCheck && (
            <div className="metadata">
              <span>Practice: {displayData?.practiceCode || 'N/A'}</span>
              <span>Location: {displayData?.locationCode || 'N/A'}</span>
            </div>
          )}
        </div>
        {!isNewCheck && (
          <div className="header-right">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="navigation-arrows">
                  <button 
                    className="nav-arrow-btn"
                    onClick={handlePreviousDrl}
                    disabled={navigationLoading || (currentIndex <= 0 && ditDrlIds.length > 0)}
                    title="Previous DRL"
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Previous</span>
                  </button>
                  <button 
                    className="nav-arrow-btn"
                    onClick={handleNextDrl}
                    disabled={navigationLoading || (currentIndex >= ditDrlIds.length - 1 && ditDrlIds.length > 0)}
                    title="Next DRL"
                  >
                    <span>Next</span>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <button 
                  className="activity-btn" 
                  onClick={() => setShowActivityDrawer(true)}
                  title="View Activity History"
                >
                  <MdOutlineHistory size={16} />
                  <span>Activity</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Financial Summary Card */}
      {!isNewCheck && (
          <div className="financial-summary-card merged">
          <div className="financial-sections">
            <div className="financial-section">
              <h3 className="section-title">DRL Summary</h3>
              <div className="financial-grid posting-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {/* Row 1 */}
                <div className="financial-item">
                  <span className="financial-label">DRL Received Date</span>
                  <span className="financial-value">
                    {displayData.drlReceivedDate ? formatDateUS(displayData.drlReceivedDate) : 'N/A'}
                  </span>
                </div>
                <div className="financial-item">
                  <span className="financial-label">DRL Created Date</span>
                  <span className="financial-value">
                    {displayData.createdAt ? formatDateUS(displayData.createdAt.split('T')[0]) : 'N/A'}
                  </span>
                </div>
                {/* Row 2 */}
                <div className="financial-item">
                  <span className="financial-label">DRL Completed Date</span>
                  <span className="financial-value">
                    {displayData.completedDate ? formatDateUS(displayData.completedDate) : 'N/A'}
                  </span>
                </div>
                <div className="financial-item">
                  <span className="financial-label">DRL Reconciled Date</span>
                  <span className="financial-value">
                    {displayData.reconciliationDate ? formatDateUS(displayData.reconciliationDate) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="financial-section posting-section">
              <h3 className="section-title">Posting Summary</h3>
              <div className="financial-grid posting-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {/* Row 1 */}
                <div className="financial-item">
                  <span className="financial-label">Total DRL Amount</span>
                  <span className={`financial-value ${(displayData.totalDrlAmount || 0) < 0 ? 'negative' : 'primary'}`}>
                    {formatCurrency(displayData.totalDrlAmount || 0)}
                  </span>
                </div>
                <div className="financial-item">
                  <span className="financial-label">Total Received in Bank</span>
                  <span className={`financial-value ${(displayData.checksAmount || 0) < 0 ? 'negative' : 'success'}`}>
                    {formatCurrency(displayData.checksAmount || 0)}
                  </span>
                </div>
                <div className="financial-item">
                  <span className="financial-label">Remaining in Bank</span>
                  <span className={`financial-value ${((displayData.totalDrlAmount || 0) - (displayData.checksAmount || 0)) < 0 ? 'negative' : ''}`}>
                    {formatCurrency((displayData.totalDrlAmount || 0) - (displayData.checksAmount || 0))}
                  </span>
                </div>
                {/* Row 2 */}
                <div className="financial-item">
                  <span className="financial-label">Total Posting</span>
                  <span className={`financial-value ${((displayData.onshorePostingAmount || 0) + (displayData.offshorePostingAmount || 0)) < 0 ? 'negative' : 'success'}`}>
                    {formatCurrency((displayData.onshorePostingAmount || 0) + (displayData.offshorePostingAmount || 0))}
                  </span>
                </div>
                <div className="financial-item">
                  <span className="financial-label">Other Amount</span>
                  <span className={`financial-value ${(displayData.otherAmount || 0) < 0 ? 'negative' : ''}`}>
                    {formatCurrency(displayData.otherAmount || 0)}
                  </span>
                </div>
                <div className="financial-item">
                  <span className="financial-label">Remaining Amount</span>
                  <span className={`financial-value ${(displayData.remainingAmount || 0) < 0 ? 'negative' : ''}`}>
                    {formatCurrency(displayData.remainingAmount || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          {!isNewCheck && (
            <>
              <button 
                className={`tab ${activeTab === 'batches' ? 'active' : ''}`}
                onClick={() => setActiveTab('batches')}
              >
                Batches ({displayData.batches?.length || 0})
              </button>
              <button 
                className={`tab ${activeTab === 'clarifications' ? 'active' : ''}`}
                onClick={() => setActiveTab('clarifications')}
              >
                Clarifications ({displayData.clarifications?.length || 0})
              </button>
            </>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="details-card">
              <div className="card-header-with-action">
                <h3 className="card-title">DRL Details</h3>
                {!isEditMode && !isNewCheck ? (
                  <PermissionGuard permission={PERMISSIONS.DIT_DRL_UPDATE}>
                    <button className="btn-primary" onClick={handleEdit}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M11 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H15C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M14.5 1.5L18.5 5.5L11 13H7V9L14.5 1.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Edit
                    </button>
                  </PermissionGuard>
                ) : (
                  <div className="edit-actions-header">
                    <button className="btn-cancel" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button className="btn-save" onClick={handleSave} disabled={isSaving}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M17 3H3C2.44772 3 2 3.44772 2 4V16C2 16.5523 2.44772 17 3 17H17C17.5523 17 18 16.5523 18 16V4C18 3.44772 17.5523 3 17 3Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M6 9L9 12L14 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Row 1: Total Amount, Credit Card, Paper Check, eCheck */}
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="form-group">
                  <label>Total DRL Amount</label>
                  <input 
                    type="text" 
                    value={formatCurrency(displayData?.totalDrlAmount || 0)} 
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Credit Card Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.creditCardAmount !== undefined && formData.creditCardAmount !== null
                            ? formData.creditCardAmount
                            : (displayData?.creditCardAmount || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.creditCardAmount !== undefined && formData.creditCardAmount !== null
                          ? formData.creditCardAmount
                          : (displayData?.creditCardAmount || 0);
                        if (currentValue === 0) {
                          handleFormChange('creditCardAmount', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('creditCardAmount', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('creditCardAmount', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('creditCardAmount', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.creditCardAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Paper Check Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.paperCheckAmount !== undefined && formData.paperCheckAmount !== null
                            ? formData.paperCheckAmount
                            : (displayData?.paperCheckAmount || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.paperCheckAmount !== undefined && formData.paperCheckAmount !== null
                          ? formData.paperCheckAmount
                          : (displayData?.paperCheckAmount || 0);
                        if (currentValue === 0) {
                          handleFormChange('paperCheckAmount', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('paperCheckAmount', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('paperCheckAmount', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('paperCheckAmount', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.paperCheckAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>E-Check Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.echeckAmount !== undefined && formData.echeckAmount !== null
                            ? formData.echeckAmount
                            : (displayData?.echeckAmount || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.echeckAmount !== undefined && formData.echeckAmount !== null
                          ? formData.echeckAmount
                          : (displayData?.echeckAmount || 0);
                        if (currentValue === 0) {
                          handleFormChange('echeckAmount', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('echeckAmount', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('echeckAmount', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('echeckAmount', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.echeckAmount || 0)} disabled />
                  )}
                </div>
              </div>

              {/* Row 2: Interest, Non-AR, Medical Records Fee, Miscellaneous */}
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="form-group">
                  <label>Interest Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.interestAmount !== undefined && formData.interestAmount !== null
                            ? formData.interestAmount
                            : (displayData?.interestAmount || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.interestAmount !== undefined && formData.interestAmount !== null
                          ? formData.interestAmount
                          : (displayData?.interestAmount || 0);
                        if (currentValue === 0) {
                          handleFormChange('interestAmount', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('interestAmount', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('interestAmount', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('interestAmount', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.interestAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Non-AR Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.nonArAmount !== undefined && formData.nonArAmount !== null
                            ? formData.nonArAmount
                            : (displayData?.nonArAmount || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.nonArAmount !== undefined && formData.nonArAmount !== null
                          ? formData.nonArAmount
                          : (displayData?.nonArAmount || 0);
                        if (currentValue === 0) {
                          handleFormChange('nonArAmount', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('nonArAmount', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('nonArAmount', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('nonArAmount', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.nonArAmount || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Medical Records Fee</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.medicalRecordFee !== undefined && formData.medicalRecordFee !== null
                            ? formData.medicalRecordFee
                            : (displayData?.medicalRecordFee || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.medicalRecordFee !== undefined && formData.medicalRecordFee !== null
                          ? formData.medicalRecordFee
                          : (displayData?.medicalRecordFee || 0);
                        if (currentValue === 0) {
                          handleFormChange('medicalRecordFee', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('medicalRecordFee', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('medicalRecordFee', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('medicalRecordFee', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.medicalRecordFee || 0)} disabled />
                  )}
                </div>
                <div className="form-group">
                  <label>Miscellaneous Amount</label>
                  {isEditMode ? (
                    <input 
                      type="number" 
                      step="0.01"
                      value={
                        (() => {
                          const currentValue = formData.miscellaneousAmount !== undefined && formData.miscellaneousAmount !== null
                            ? formData.miscellaneousAmount
                            : (displayData?.miscellaneousAmount || 0);
                          // Return empty string if value is 0 to allow clean input
                          return currentValue === 0 ? '' : currentValue;
                        })()
                      }
                      onFocus={(e) => {
                        // Clear the field if it's 0 when user focuses
                        const currentValue = formData.miscellaneousAmount !== undefined && formData.miscellaneousAmount !== null
                          ? formData.miscellaneousAmount
                          : (displayData?.miscellaneousAmount || 0);
                        if (currentValue === 0) {
                          handleFormChange('miscellaneousAmount', '');
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleFormChange('miscellaneousAmount', '');
                        } else {
                          const numVal = parseFloat(val);
                          handleFormChange('miscellaneousAmount', isNaN(numVal) ? '' : numVal);
                        }
                      }}
                      onBlur={(e) => {
                        // Set to 0 if empty on blur
                        const val = e.target.value;
                        if (val === '' || val === null || val === undefined) {
                          handleFormChange('miscellaneousAmount', 0);
                        }
                      }}
                      placeholder="0"
                    />
                  ) : (
                    <input type="text" value={formatCurrency(displayData?.miscellaneousAmount || 0)} disabled />
                  )}
                </div>
              </div>

              {/* Row 3: Posted Onshore, Posted Offshore, Assignee, Reportee */}
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="form-group">
                  <label>Posted Onshore</label>
                  <input 
                    type="text" 
                    value={formatCurrency(displayData?.onshorePostingAmount || 0)} 
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Posted Offshore</label>
                  <input 
                    type="text" 
                    value={formatCurrency(displayData?.offshorePostingAmount || 0)} 
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Assignee</label>
                  {isEditMode ? (
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Assignee' },
                        ...(users || []).map(user => ({
                          value: user.userId || user.id,
                          label: user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.email || 'Unknown User'
                        }))
                      ]}
                      value={formData.assigneeId || ''}
                      onChange={(val) => handleFormChange('assigneeId', val)}
                      placeholder="Select Assignee"
                      maxVisibleItems={5}
                    />
                  ) : (
                    <input 
                      type="text" 
                      value={displayData?.assigneeId ? (getUserName ? getUserName(displayData.assigneeId) : 'N/A') : 'N/A'}
                      disabled
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Reportee</label>
                  {isEditMode ? (
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Reportee' },
                        ...(users || []).map(user => ({
                          value: user.userId || user.id,
                          label: user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.email || 'Unknown User'
                        }))
                      ]}
                      value={formData.reporteeId || ''}
                      onChange={(val) => handleFormChange('reporteeId', val)}
                      placeholder="Select Reportee"
                      maxVisibleItems={5}
                    />
                  ) : (
                    <input 
                      type="text" 
                      value={displayData?.reporteeId ? (getUserName ? getUserName(displayData.reporteeId) : 'N/A') : 'N/A'}
                      disabled
                    />
                  )}
                </div>
              </div>

              {/* Row 4: TAT (Turnaround Time) - Completion TAT and Reconciliation TAT */}
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="form-group">
                  <label>Completion TAT (Created Date)</label>
                  <input 
                    type="text" 
                    value={formatTAT(calculateTAT(displayData?.completedDate, displayData?.createdAt))}
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Completion TAT (DRL Date)</label>
                  <input 
                    type="text" 
                    value={formatTAT(calculateTAT(displayData?.completedDate, displayData?.drlReceivedDate))}
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Reconciliation TAT (Created Date)</label>
                  <input 
                    type="text" 
                    value={formatTAT(calculateTAT(displayData?.reconciliationDate, displayData?.createdAt))}
                    disabled 
                  />
                </div>
                <div className="form-group">
                  <label>Reconciliation TAT (DRL Date)</label>
                  <input 
                    type="text" 
                    value={formatTAT(calculateTAT(displayData?.reconciliationDate, displayData?.drlReceivedDate))}
                    disabled 
                  />
                </div>
              </div>

              {/* Row 5: Notes (full width) */}
              <div className="form-grid form-row-1">
                <div className="form-group">
                  <label>Notes</label>
                  <textarea 
                    value={isEditMode ? formData.drlNotes || '' : displayData?.drlNotes || ''}
                    onChange={(e) => handleFormChange('drlNotes', e.target.value)}
                    disabled={!isEditMode}
                    rows="3"
                  />
                </div>
              </div>
            </div>
            
          </div>
        )}

        {/* Batches Tab */}
        {!isNewCheck && activeTab === 'batches' && (
          <div className="tab-content">
            <div className="batches-card">
              <div className="card-header">
                <h3 className="card-title">Batches</h3>
                {batchModalMode !== 'add' && !editingBatchId && (
                  <PermissionGuard permission={PERMISSIONS.DIT_DRL_BATCH_CREATE}>
                    <button className="btn-primary" onClick={() => {
                      setBatchFormData({
                        batchRunNumber: '',
                        batchNumber: '',
                        batchDate: '',
                        batchType: 'MANUAL',
                        batchAmount: '',
                        batchNotes: '',
                        invoiceNumber: '',
                        edmNumber: '',
                        batchSource: 'ONSHORE'
                      });
                      setViewingBatchId('new'); // Use 'new' as a special ID for add mode
                      setBatchModalMode('add');
                      setBatchValidationErrors({});
                    }}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Add Batch
                    </button>
                  </PermissionGuard>
                )}
              </div>

              {/* Batches Table */}
              <div className="batches-table-wrapper">
                <table className="batches-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSortBatches('batchType')}>
                        Batch Type {getSortArrow('batchType')}
                      </th>
                      <th className="sortable" onClick={() => handleSortBatches('batchNumber')}>
                        Batch Number {getSortArrow('batchNumber')}
                      </th>
                      <th className="sortable" onClick={() => handleSortBatches('batchRunNumber')}>
                        Run Number {getSortArrow('batchRunNumber')}
                      </th>
                      <th>Notes</th>
                      <th className="sortable" onClick={() => handleSortBatches('edmNumber')}>
                        EDM Number {getSortArrow('edmNumber')}
                      </th>
                      <th className="sortable" onClick={() => handleSortBatches('batchAmount')}>
                        Amount {getSortArrow('batchAmount')}
                      </th>
                      <th>All Check Allocated $</th>
                      <th>Batch Allocation</th>
                      <th className="sortable" onClick={() => handleSortBatches('updatedAt')}>
                        Updated At {getSortArrow('updatedAt')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ditDrl.batches && ditDrl.batches.length > 0 ? (
                      getSortedBatches().map((batch) => (
                        <React.Fragment key={batch.batchId}>
                          <tr 
                            data-batch-id={batch.batchId} 
                            className={`${batch.archived ? 'batch-archived' : ''} ${batch.invoiceNumber ? 'batch-unknown-indicator' : ''}`}
                          >
                            <td>{batch.batchType || 'N/A'}</td>
                            <td><TruncatedText text={batch.batchNumber} maxLength={15} /></td>
                            <td><TruncatedText text={batch.batchRunNumber} maxLength={15} /></td>
                            <td><TruncatedText text={batch.batchNotes} maxLength={15} /></td>
                            <td><TruncatedText text={batch.edmNumber} maxLength={15} /></td>
                            <td>{formatCurrency(batch.batchAmount)}</td>
                            <td>
                              {hasAllocations(batch) ? (
                                <span style={{ fontWeight: '600', color: '#065f46' }}>
                                  {formatCurrency(getTotalAllocatedAmount(batch))}
                                </span>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>$0.00</span>
                              )}
                            </td>
                            <td>
                              <div className="batch-allocation-cell" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedBatchForCheckAllocations(batch);
                                    setShowCheckBatchAllocationsModal(true);
                                  }}
                                  className="allocation-indicator"
                                  title={`${getAllocationCount(batch)} check batch(es) allocated - Click to view details`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '6px 12px',
                                    backgroundColor: '#d1fae5',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#065f46',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    minWidth: '40px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#10b981';
                                    e.target.style.color = 'white';
                                    e.target.style.transform = 'scale(1.05)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#d1fae5';
                                    e.target.style.color = '#065f46';
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                >
                                  {getAllocationCount(batch)}
                                </button>
                                <div className="allocation-actions" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <button 
                                    className="btn-icon allocate"
                                    onClick={() => handleAllocateClick(batch.batchId)}
                                    title="Allocate Check Batch"
                                    style={{ 
                                      color: '#3b82f6',
                                      padding: '6px',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '4px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#eff6ff';
                                      e.target.style.borderColor = '#3b82f6';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'white';
                                      e.target.style.borderColor = '#e5e7eb';
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  <button
                                    className="btn-icon deallocate"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedBatchForDeallocation(batch);
                                      setShowDeallocateSelectionModal(true);
                                    }}
                                    title={hasAllocations(batch) ? `Deallocate check batch - ${batch.allocations.length} allocation(s)` : 'No allocations to remove'}
                                    style={{
                                      color: '#ef4444',
                                      padding: '6px',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '4px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#fef2f2';
                                      e.target.style.borderColor = '#ef4444';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'white';
                                      e.target.style.borderColor = '#e5e7eb';
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      <path d="M7 10H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M10 17C13.866 17 17 13.866 17 10C17 6.13401 13.866 3 10 3C6.13401 3 3 6.13401 3 10C3 13.866 6.13401 17 10 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td>
                              <UserTimestamp
                                userId={batch.updatedBy}
                                dateTime={batch.updatedAt}
                                action="updated"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                            <td>
                              <div className="inline-actions">
                                <button 
                                  className="btn-icon view"
                                  onClick={() => {
                                    setViewingBatchId(batch.batchId);
                                    setBatchModalMode('view');
                                  }}
                                  title="View Details"
                                >
                                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 10s4-6 9-6 9 6 9 6-4 6-9 6-9-6-9-6z"/>
                                    <circle cx="10" cy="10" r="3"/>
                                  </svg>
                                </button>
                                <PermissionGuard permission={PERMISSIONS.DIT_DRL_BATCH_UPDATE}>
                                  <button 
                                    className="btn-icon edit"
                                    onClick={() => {
                                      handleEditBatch(batch);
                                      setViewingBatchId(batch.batchId);
                                      setBatchModalMode('edit');
                                    }}
                                    title="Edit"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      <path d="M11 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H15C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                      <path d="M14.5 1.5L18.5 5.5L11 13H7V9L14.5 1.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </PermissionGuard>
                                <PermissionGuard permission={PERMISSIONS.DIT_DRL_BATCH_UPDATE}>
                                  <button 
                                    className={`btn-icon archive ${batch.archived ? 'archived' : ''}`}
                                    onClick={() => handleArchiveBatchClick(batch.batchId, batch.archived)}
                                    title={batch.archived ? 'Unarchive' : 'Archive'}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      {batch.archived ? (
                                        <path d="M3 4H17M4 4V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H14C14.5304 19 15.0391 18.7893 15.4142 18.4142C15.7893 18.0391 16 17.5304 16 17V4M7 8L10 11L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      ) : (
                                        <path d="M3 4H17M4 4V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H14C14.5304 19 15.0391 18.7893 15.4142 18.4142C15.7893 18.0391 16 17.5304 16 17V4M7 8L10 5L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      )}
                                    </svg>
                                  </button>
                                </PermissionGuard>
                                <PermissionGuard permission={PERMISSIONS.DIT_DRL_BATCH_DELETE}>
                                  <button 
                                    className="btn-icon delete"
                                    onClick={() => handleDeleteBatch(batch.batchId, batch.archived)}
                                    title="Delete"
                                    style={{ color: '#dc2626' }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                      <path d="M3 6H5H17M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </PermissionGuard>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="empty-state">No batches found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Clarifications Tab */}
        {!isNewCheck && activeTab === 'clarifications' && (
          <div className="tab-content">
            <div className="clarifications-card">
              <div className="card-header">
                <h3 className="card-title">Clarifications</h3>
                {!isAddingClarification && !editingClarificationId && (
                  <PermissionGuard permission={PERMISSIONS.DIT_DRL_CLARIFICATION_CREATE}>
                    <button className="btn-primary" onClick={() => setIsAddingClarification(true)}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Add Clarification
                    </button>
                  </PermissionGuard>
                )}
              </div>

              {/* Add Clarification Form */}
              {isAddingClarification && (
                <div className="clarification-form-card">
                  <h4>Add New Clarification</h4>
                  {/* Row 1: Clarification Type, Assignee, Reporter */}
                  <div className="form-grid form-row-3">
                    <div className="form-group">
                      <label>Clarification Type *</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Clarification Type' },
                          ...clarificationTypes.map(ct => ({
                            value: ct.code,
                            label: ct.name
                          }))
                        ]}
                        value={clarificationFormData.clarificationType}
                        onChange={(value) => handleClarificationFormChange('clarificationType', value)}
                        placeholder="Select Clarification Type"
                        maxVisibleItems={5}
                        disabled={clarificationTypesLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>Assignee</label>
                      <select 
                        value={clarificationFormData.assignee || 'ON-SHORE'}
                        onChange={(e) => handleClarificationFormChange('assignee', e.target.value)}
                      >
                        <option value="ON-SHORE">ON-SHORE</option>
                        <option value="OFF-SHORE">OFF-SHORE</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Reporter</label>
                      <select 
                        value={clarificationFormData.reportee || 'OFF-SHORE'}
                        onChange={(e) => handleClarificationFormChange('reportee', e.target.value)}
                      >
                        <option value="ON-SHORE">ON-SHORE</option>
                        <option value="OFF-SHORE">OFF-SHORE</option>
                      </select>
                    </div>
                  </div>
                  {/* Row 2: Status, Amount, Assignee Name, Reporter Name */}
                  <div className="form-grid form-row-4">
                    <div className="form-group">
                      <label>Status</label>
                      <select 
                        value={clarificationFormData.status}
                        onChange={(e) => handleClarificationFormChange('status', e.target.value)}
                      >
                        <option value="OPEN">Open</option>
                        <option value="RESOLVED">Resolved</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Amount (Optional)</label>
                      <input 
                        type="number" 
                        step="0.01"
                        value={clarificationFormData.amount || ''}
                        onChange={(e) => handleClarificationFormChange('amount', e.target.value)}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div className="form-group">
                      <label>Assignee Name</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Assignee' },
                          ...(users || []).map(user => ({
                            value: user.userId || user.id,
                            label: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}` 
                              : user.email || 'Unknown User'
                          }))
                        ]}
                        value={clarificationFormData.assigneeId || ''}
                        onChange={(value) => handleClarificationFormChange('assigneeId', value)}
                        placeholder="Select Assignee"
                        maxVisibleItems={5}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reporter Name</label>
                      <SearchableDropdown
                        options={[
                          { value: '', label: 'Select Reporter' },
                          ...(users || []).map(user => ({
                            value: user.userId || user.id,
                            label: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}` 
                              : user.email || 'Unknown User'
                          }))
                        ]}
                        value={clarificationFormData.reporterId || ''}
                        onChange={(value) => handleClarificationFormChange('reporterId', value)}
                        placeholder="Select Reporter"
                        maxVisibleItems={5}
                      />
                    </div>
                  </div>
                  <div className="form-grid form-row-1">
                    <div className="form-group full-width textarea-with-expand">
                      <label>Details</label>
                      <div className="textarea-wrapper">
                        <textarea 
                          value={clarificationFormData.details}
                          onChange={(e) => handleClarificationFormChange('details', e.target.value)}
                          rows={expandedTextareas['create-details'] ? 4 : 1}
                          placeholder="Enter clarification details"
                          className={expandedTextareas['create-details'] ? '' : 'textarea-single-row'}
                        />
                        <button
                          type="button"
                          className="textarea-expand-btn"
                          onClick={() => setExpandedTextareas(prev => ({
                            ...prev,
                            'create-details': !prev['create-details']
                          }))}
                          title={expandedTextareas['create-details'] ? 'Collapse' : 'Expand'}
                        >
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                            {expandedTextareas['create-details'] ? (
                              <path d="M5 12.5L10 7.5L15 12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            ) : (
                              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            )}
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button className="btn-cancel" onClick={handleCancelClarification}>Cancel</button>
                    <button className="btn-save" onClick={handleAddClarification} disabled={isCreatingClarification}>
                      {isCreatingClarification ? 'Creating...' : 'Save Clarification'}
                    </button>
                  </div>
                </div>
              )}

              {/* Clarifications List */}
              {ditDrl?.clarifications && ditDrl.clarifications.length > 0 ? (
                <div className="clarifications-list-redesigned">
                  {ditDrl.clarifications.map((clarification) => (
                    <div 
                      key={clarification.clarificationId}
                      className={`clarification-item ${editingClarificationId === clarification.clarificationId ? 'editing' : ''}`}
                      data-clarification-id={clarification.clarificationId}
                    >
                      {editingClarificationId === clarification.clarificationId ? (
                        // Edit Mode
                        <div className="clarification-edit-form">
                          <h4>Edit Clarification</h4>
                          {/* Row 1: Clarification Type, Assignee, Reporter */}
                          <div className="form-grid form-row-3">
                            <div className="form-group">
                              <label>Clarification Type *</label>
                              <SearchableDropdown
                                options={[
                                  { value: '', label: 'Select Clarification Type' },
                                  ...clarificationTypes.map(ct => ({
                                    value: ct.code,
                                    label: ct.name
                                  }))
                                ]}
                                value={clarificationFormData.clarificationType}
                                onChange={(value) => handleClarificationFormChange('clarificationType', value)}
                                placeholder="Select Clarification Type"
                                maxVisibleItems={5}
                                disabled={clarificationTypesLoading}
                              />
                            </div>
                            <div className="form-group">
                              <label>Assignee</label>
                              <select 
                                value={clarificationFormData.assignee || 'ON-SHORE'}
                                onChange={(e) => handleClarificationFormChange('assignee', e.target.value)}
                              >
                                <option value="ON-SHORE">ON-SHORE</option>
                                <option value="OFF-SHORE">OFF-SHORE</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Reporter</label>
                              <select 
                                value={clarificationFormData.reportee || 'OFF-SHORE'}
                                onChange={(e) => handleClarificationFormChange('reportee', e.target.value)}
                              >
                                <option value="ON-SHORE">ON-SHORE</option>
                                <option value="OFF-SHORE">OFF-SHORE</option>
                              </select>
                            </div>
                          </div>
                          {/* Row 2: Status, Amount, Assignee Name, Reporter Name */}
                          <div className="form-grid form-row-4">
                            <div className="form-group">
                              <label>Status</label>
                              <select 
                                value={clarificationFormData.status}
                                onChange={(e) => handleClarificationFormChange('status', e.target.value)}
                              >
                                <option value="OPEN">Open</option>
                                <option value="RESOLVED">Resolved</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Amount (Optional)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                value={clarificationFormData.amount || ''}
                                onChange={(e) => handleClarificationFormChange('amount', e.target.value)}
                                placeholder="Enter amount"
                              />
                            </div>
                            <div className="form-group">
                              <label>Assignee Name</label>
                              <SearchableDropdown
                                options={[
                                  { value: '', label: 'Select Assignee' },
                                  ...(users || []).map(user => ({
                                    value: user.userId || user.id,
                                    label: user.firstName && user.lastName 
                                      ? `${user.firstName} ${user.lastName}` 
                                      : user.email || 'Unknown User'
                                  }))
                                ]}
                                value={clarificationFormData.assigneeId || ''}
                                onChange={(value) => handleClarificationFormChange('assigneeId', value)}
                                placeholder="Select Assignee"
                                maxVisibleItems={5}
                              />
                            </div>
                            <div className="form-group">
                              <label>Reporter Name</label>
                              <SearchableDropdown
                                options={[
                                  { value: '', label: 'Select Reporter' },
                                  ...(users || []).map(user => ({
                                    value: user.userId || user.id,
                                    label: user.firstName && user.lastName 
                                      ? `${user.firstName} ${user.lastName}` 
                                      : user.email || 'Unknown User'
                                  }))
                                ]}
                                value={clarificationFormData.reporterId || ''}
                                onChange={(value) => handleClarificationFormChange('reporterId', value)}
                                placeholder="Select Reporter"
                                maxVisibleItems={5}
                              />
                            </div>
                          </div>
                          <div className="form-grid form-row-1">
                            <div className="form-group full-width textarea-with-expand">
                              <label>Details</label>
                              <div className="textarea-wrapper">
                                <textarea 
                                  value={clarificationFormData.details}
                                  onChange={(e) => handleClarificationFormChange('details', e.target.value)}
                                  rows={expandedTextareas[`edit-details-${editingClarificationId}`] ? 4 : 1}
                                  className={expandedTextareas[`edit-details-${editingClarificationId}`] ? '' : 'textarea-single-row'}
                                />
                                <button
                                  type="button"
                                  className="textarea-expand-btn"
                                  onClick={() => setExpandedTextareas(prev => ({
                                    ...prev,
                                    [`edit-details-${editingClarificationId}`]: !prev[`edit-details-${editingClarificationId}`]
                                  }))}
                                  title={expandedTextareas[`edit-details-${editingClarificationId}`] ? 'Collapse' : 'Expand'}
                                >
                                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                    {expandedTextareas[`edit-details-${editingClarificationId}`] ? (
                                      <path d="M5 12.5L10 7.5L15 12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    ) : (
                                      <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    )}
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="form-actions">
                            <button className="btn-cancel" onClick={handleCancelClarification}>Cancel</button>
                            <button className="btn-save" onClick={handleUpdateClarification} disabled={isUpdatingClarification}>
                              {isUpdatingClarification ? 'Updating...' : 'Update'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          {/* Row 1: Type (before value), Status, Assignee, Reporter, Opened, Actions */}
                          <div className="clarification-row-1">
                            <div className="clarification-main-info">
                              <span className="clarification-type-label">Type:</span>
                              <span className="clarification-type">{clarification.clarificationType || 'N/A'}</span>
                              <span className={`clarification-status ${clarification.status?.toLowerCase()}`}>
                                {clarification.status || 'OPEN'}
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Amount:</span>
                                <span className="field-value-inline">{clarification.amount !== undefined && clarification.amount !== null ? clarification.amount : 0}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Assignee:</span>
                                <span className="field-value-inline">{clarification.assignee || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Reporter:</span>
                                <span className="field-value-inline">{clarification.reportee || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Assignee Name:</span>
                                <span className="field-value-inline">{getUserName(clarification.assigneeId) || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Reporter Name:</span>
                                <span className="field-value-inline">{getUserName(clarification.reporterId) || 'N/A'}</span>
                              </span>
                              <span className="clarification-field-inline">
                                <span className="field-label-inline">Opened:</span>
                                <span className="field-value-inline">{clarification.openedAt ? formatDateUS(clarification.openedAt.split('T')[0]) : 'N/A'}</span>
                              </span>
                              {clarification.resolvedAt && (
                                <span className="clarification-field-inline">
                                  <span className="field-label-inline">Resolved:</span>
                                  <span className="field-value-inline">{formatDateUS(clarification.resolvedAt.split('T')[0])}</span>
                                </span>
                              )}
                            </div>
                            <div className="clarification-actions">
                              <PermissionGuard permission={PERMISSIONS.DIT_DRL_CLARIFICATION_UPDATE}>
                                <button 
                                  className="btn-icon edit"
                                  onClick={() => handleEditClarification(clarification)}
                                  title="Edit"
                                >
                                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                    <path d="M11 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H15C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M14.5 1.5L18.5 5.5L11 13H7V9L14.5 1.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </PermissionGuard>
                            </div>
                          </div>

                          {/* Row 2: Details */}
                          <div className="clarification-row-2">
                            <div className="clarification-details-full">
                              <span className="field-label-inline">Details:</span>
                              <span className="field-value-inline">{clarification.details || 'No details provided'}</span>
                            </div>
                          </div>

                          {/* Row 3: Comments Section (minimized by default) */}
                          <div className="clarification-row-3">
                            <button 
                              className="comments-toggle-btn-below"
                              onClick={() => toggleCommentsExpanded(clarification.clarificationId)}
                              title={expandedCommentsClarificationId === clarification.clarificationId ? 'Hide comments' : 'Show comments'}
                            >
                              <span className="comments-label">Comments</span>
                              <span className="comments-count">({clarification.comments?.length || 0})</span>
                              <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 20 20" 
                                fill="none"
                                className={`comments-arrow ${expandedCommentsClarificationId === clarification.clarificationId ? 'expanded' : ''}`}
                              >
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            
                            {expandedCommentsClarificationId === clarification.clarificationId && (
                              <div className="comments-section-expanded">
                                <div className="comments-list">
                                  {clarification.comments && clarification.comments.length > 0 ? (
                                    clarification.comments.map((comment) => (
                                      <div key={comment.commentId} className="comment-item-message">
                                        <div className="comment-header">
                                          <span className="comment-author">{comment.userDisplayName || 'Unknown'}</span>
                                          <span className="comment-date">{formatDateTime(comment.commentedAt)}</span>
                                        </div>
                                        <div className="comment-body">
                                          <span className="comment-text">{comment.comment}</span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="comments-empty-state">No comments yet</div>
                                  )}
                                </div>
                                
                                {/* Add Comment Form */}
                                <div className="add-comment-form">
                                  <div className="comment-form-grid">
                                    <div className="comment-form-row comment-textarea-row">
                                      <textarea 
                                        placeholder="Add a comment..."
                                        value={commentText[clarification.clarificationId] || ''}
                                        onChange={(e) => {
                                          const filteredValue = filterEmojis(e.target.value);
                                          setCommentText(prev => ({
                                            ...prev,
                                            [clarification.clarificationId]: filteredValue
                                          }));
                                          // Auto-resize textarea only if it has content
                                          if (filteredValue.trim()) {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.max(36, e.target.scrollHeight) + 'px';
                                          } else {
                                            e.target.style.height = '36px';
                                          }
                                        }}
                                        onFocus={(e) => {
                                          // Allow expansion on focus if there's content
                                          if (e.target.value.trim()) {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.max(36, e.target.scrollHeight) + 'px';
                                          }
                                        }}
                                        onBlur={(e) => {
                                          // Collapse to single row if empty
                                          if (!e.target.value.trim()) {
                                            e.target.style.height = '36px';
                                          }
                                        }}
                                        rows="1"
                                        className="comment-textarea-single-line"
                                      />
                                      <div className="comment-assignee-select">
                                        <label className="comment-assignee-label">Assignee</label>
                                        <select 
                                          value={commentAssignee[clarification.clarificationId] !== undefined 
                                            ? (commentAssignee[clarification.clarificationId] || '') 
                                            : (clarification.assignee || '')}
                                          onChange={(e) => {
                                            const selectedValue = e.target.value || undefined;
                                            setCommentAssignee(prev => ({
                                              ...prev,
                                              [clarification.clarificationId]: selectedValue
                                            }));
                                            // Auto-set reporter based on assignee
                                            if (selectedValue === 'ON-SHORE') {
                                              setCommentReportee(prev => ({
                                                ...prev,
                                                [clarification.clarificationId]: 'OFF-SHORE'
                                              }));
                                            } else if (selectedValue === 'OFF-SHORE') {
                                              setCommentReportee(prev => ({
                                                ...prev,
                                                [clarification.clarificationId]: 'ON-SHORE'
                                              }));
                                            } else {
                                              // Clear reporter if assignee is cleared
                                              setCommentReportee(prev => {
                                                const updated = { ...prev };
                                                delete updated[clarification.clarificationId];
                                                return updated;
                                              });
                                            }
                                          }}
                                        >
                                          <option value="ON-SHORE">ON-SHORE</option>
                                          <option value="OFF-SHORE">OFF-SHORE</option>
                                        </select>
                                      </div>
                                    </div>
                                    <div className="comment-form-row">
                                      <button 
                                        className={`btn-secondary ${(commentText[clarification.clarificationId] || '').trim() ? 'btn-comment-active' : ''}`}
                                        onClick={() => handleAddComment(clarification.clarificationId)}
                                      >
                                        Add Comment
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No clarifications found</div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Archive/Unarchive Confirmation Modal */}
      {showArchiveConfirm && (
        <div 
          className="modal-overlay"
          onClick={handleArchiveBatchCancel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              {archiveBatchIsArchived ? 'Unarchive Batch' : 'Archive Batch'}
            </h3>
            <p style={{ marginBottom: '24px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to {archiveBatchIsArchived ? 'unarchive' : 'archive'} this batch?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-cancel" 
                onClick={handleArchiveBatchCancel}
              >
                Cancel
              </button>
              <button 
                className="btn-save" 
                onClick={handleArchiveBatchConfirm}
              >
                {archiveBatchIsArchived ? 'Unarchive' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="modal-overlay"
          onClick={handleDeleteConfirmCancel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#DC2626' }}>
                  <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                Delete Batch
              </h3>
            </div>
            <p style={{ marginBottom: '24px', marginLeft: '52px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Are you sure you want to delete this batch? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-cancel" 
                onClick={handleDeleteConfirmCancel}
              >
                Cancel
              </button>
              <button 
                className="btn-save" 
                onClick={handleDeleteConfirm}
                style={{ background: '#DC2626', color: 'white' }}
                onMouseEnter={(e) => e.target.style.background = '#B91C1C'}
                onMouseLeave={(e) => e.target.style.background = '#DC2626'}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Error Modal */}
      {showValidationError && (
        <div 
          className="modal-overlay"
          onClick={() => setShowValidationError(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#DC2626' }}>
                  <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                Validation Failed
              </h3>
            </div>
            <div style={{ marginBottom: '24px', marginLeft: '52px' }}>
              <p style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                The following errors were found:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626', fontSize: '14px', lineHeight: '1.8' }}>
                {validationErrorMessages.map((message, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{message}</li>
                ))}
              </ul>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn-primary" 
                onClick={() => setShowValidationError(false)}
                style={{ minWidth: '80px' }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Allocation Modal */}
      {showAllocationModal && (
        <div 
          className="modal-overlay"
          onClick={() => {
            setShowAllocationModal(false);
            setAllocationSearchError('');
            setAllocationSearchPerformed(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflowY: 'auto',
            padding: '16px'
          }}
        >
          <div 
            className="modal-content allocation-modal-wrapper"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '12px 14px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Allocate Check Batch to DRL Batch
              </h3>
              <button
                onClick={() => {
                  setShowAllocationModal(false);
                  setAllocationSearchError('');
                  setAllocationSearchPerformed(false);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => e.target.style.color = '#111827'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Search section: single row - title + Practice + Location, then one row for filters + Search */}
            <div style={{ marginBottom: '10px', padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', position: 'relative', zIndex: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px 16px', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Search Check Batches</span>
                <span style={{ color: '#d1d5db', fontSize: '12px' }}>|</span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Practice: <strong style={{ color: '#075985' }}>{ditDrl?.practiceCode || 'N/A'}</strong></span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Location: <strong style={{ color: '#075985' }}>{ditDrl?.locationCode || 'N/A'}</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ minWidth: '110px' }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontSize: '11px', fontWeight: '500', color: '#374151' }}>Deposit Date</label>
                  <USDateInput
                    name="depositDate"
                    value={allocationFilters.depositDate}
                    onChange={(e) => {
                      setAllocationFilters(prev => ({ ...prev, depositDate: e.target.value }));
                      setAllocationSearchError('');
                      setAllocationSearchPerformed(false);
                    }}
                    className="allocation-date-input"
                  />
                </div>
                <div style={{ minWidth: '120px', flex: '1', maxWidth: '160px' }}>
                  <label style={{ display: 'block', marginBottom: '2px', fontSize: '11px', fontWeight: '500', color: '#374151' }}>Batch Number</label>
                  <input
                    type="text"
                    value={allocationFilters.batchNumber}
                    onChange={(e) => {
                      setAllocationFilters(prev => ({ ...prev, batchNumber: e.target.value }));
                      setAllocationSearchError('');
                      setAllocationSearchPerformed(false);
                    }}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  onClick={handleSearchCheckBatches}
                  disabled={(!allocationFilters.depositDate?.trim() && !allocationFilters.batchNumber?.trim()) || searchCheckBatchesLoading}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: ((!allocationFilters.depositDate?.trim() && !allocationFilters.batchNumber?.trim()) || searchCheckBatchesLoading) ? 'not-allowed' : 'pointer',
                    opacity: ((!allocationFilters.depositDate?.trim() && !allocationFilters.batchNumber?.trim()) || searchCheckBatchesLoading) ? 0.5 : 1,
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if ((allocationFilters.depositDate?.trim() || allocationFilters.batchNumber?.trim()) && !searchCheckBatchesLoading) {
                      e.target.style.backgroundColor = '#2563eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((allocationFilters.depositDate?.trim() || allocationFilters.batchNumber?.trim()) && !searchCheckBatchesLoading) {
                      e.target.style.backgroundColor = '#3b82f6';
                    }
                  }}
                >
                  {searchCheckBatchesLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              {allocationSearchError ? (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#dc2626', 
                  marginTop: '6px',
                  padding: '8px 10px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '4px',
                  border: '1px solid #fecaca',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{allocationSearchError}</span>
                </div>
              ) : (
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>Enter at least one: deposit date and/or batch number</div>
              )}
            </div>

            {/* Scrollable Content Area */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

            {/* Check Batches Results */}
            {searchCheckBatches.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                  Select Check Batch ({searchCheckBatches.length} found)
                </h4>
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px', 
                  overflow: 'hidden',
                  maxHeight: '220px',
                  overflowY: 'auto'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>Select</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>Check #</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>Deposit Date</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>Batch Number</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchCheckBatches.map((check) => (
                        check.batches && check.batches.map((batch, idx) => (
                          <tr 
                            key={`${check.checkId}-${batch.batchId}`}
                            style={{
                              backgroundColor: selectedCheckBatch?.batchId === batch.batchId ? '#eff6ff' : (idx % 2 === 0 ? 'white' : '#f9fafb'),
                              cursor: 'pointer',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedCheckBatch?.batchId !== batch.batchId) {
                                e.target.closest('tr').style.backgroundColor = '#f3f4f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedCheckBatch?.batchId !== batch.batchId) {
                                e.target.closest('tr').style.backgroundColor = idx % 2 === 0 ? 'white' : '#f9fafb';
                              }
                            }}
                            onClick={() => {
                              setSelectedCheckId(check.checkId);
                              setSelectedCheckBatch(batch);
                              setAllocationAmount(batch.batchAmount?.toString() || '');
                            }}
                          >
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <input
                                type="radio"
                                checked={selectedCheckBatch?.batchId === batch.batchId}
                                onChange={() => {
                                  setSelectedCheckId(check.checkId);
                                  setSelectedCheckBatch(batch);
                                  setAllocationAmount(batch.batchAmount?.toString() || '');
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', color: '#111827', fontSize: '12px' }}>{check.checkNumber || 'N/A'}</td>
                            <td style={{ padding: '6px 8px', color: '#111827', fontSize: '12px' }}>{check.depositDate ? formatDateUS(check.depositDate) : 'N/A'}</td>
                            <td style={{ padding: '6px 8px', color: '#111827', fontSize: '12px' }}>{batch.batchNumber || 'N/A'}</td>
                            <td style={{ padding: '6px 8px', color: '#111827', fontWeight: '500', textAlign: 'right', fontSize: '12px' }}>
                              {formatCurrency(batch.batchAmount || 0)}
                            </td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* No Results Found Message */}
            {allocationSearchPerformed && searchCheckBatches.length === 0 && !searchCheckBatchesLoading && !allocationSearchError && (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
                    <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600', color: '#92400e' }}>
                  No Check Batches Found
                </h4>
                <p style={{ margin: '0', fontSize: '13px', color: '#78350f', lineHeight: '1.5' }}>
                  No check batches match your search criteria. Try adjusting your search filters.
                </p>
              </div>
            )}

            </div>
            {/* End of scrollable content */}

            {/* Actions - Fixed Footer */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
              <button
                onClick={() => {
                  setShowAllocationModal(false);
                  setAllocationFilters({ depositDate: '', batchNumber: '' });
                  setAllocationSearchError('');
                  setAllocationSearchPerformed(false);
                  setSearchCheckBatches([]);
                  setSelectedCheckBatch(null);
                  setSelectedCheckId(null);
                  setAllocationAmount('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleAllocateSubmit}
                disabled={!selectedCheckBatch || !allocationAmount}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: (!selectedCheckBatch || !allocationAmount) ? 'not-allowed' : 'pointer',
                  opacity: (!selectedCheckBatch || !allocationAmount) ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (selectedCheckBatch && allocationAmount) {
                    e.target.style.backgroundColor = '#059669';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCheckBatch && allocationAmount) {
                    e.target.style.backgroundColor = '#10b981';
                  }
                }}
              >
                Allocate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deallocate Selection Modal */}
      {showDeallocateSelectionModal && selectedBatchForDeallocation && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowDeallocateSelectionModal(false);
            setSelectedBatchForDeallocation(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Deallocate Check Batches - DRL Batch {selectedBatchForDeallocation.batchNumber || 'N/A'}
              </h3>
              <button
                onClick={() => {
                  setShowDeallocateSelectionModal(false);
                  setSelectedBatchForDeallocation(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => e.target.style.color = '#111827'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
              Select a check batch allocation to remove from this DRL batch
            </p>

            {selectedBatchForDeallocation.allocations && selectedBatchForDeallocation.allocations.length > 0 ? (
              <div style={{ 
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px'
                }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Check Number</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Batch Number</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Check Amount</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Allocated Amount</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Check Batch Amount</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBatchForDeallocation.allocations.map((allocation, idx) => (
                      <tr
                        key={allocation.allocationId || idx}
                        style={{
                          backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                          borderBottom: idx < selectedBatchForDeallocation.allocations.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}
                        onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = idx % 2 === 0 ? 'white' : '#f9fafb'}
                      >
                        <td style={{ padding: '10px 8px', color: '#111827' }}>
                          {allocation.checkNumber || 'N/A'}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#111827' }}>
                          {allocation.checkBatchNumber ? (
                            <button
                              onClick={() => {
                                setShowDeallocateSelectionModal(false);
                                setSelectedBatchForDeallocation(null);
                                navigate(`/checks/${allocation.checkId}?batchId=${allocation.checkBatchId}`);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#3b82f6',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: 0,
                                fontSize: '13px',
                                fontWeight: '500',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                              onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
                            >
                              {allocation.checkBatchNumber}
                            </button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                          {formatCurrency(allocation.checkTotalAmount || 0)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                          {formatCurrency(allocation.allocatedAmount || 0)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                          {formatCurrency(allocation.checkBatchAmount || 0)}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeallocateSelectionModal(false);
                              handleDeallocateClick(selectedBatchForDeallocation.batchId, allocation.checkBatchId, allocation);
                            }}
                            title="Deallocate this check batch"
                            style={{
                              color: '#dc2626',
                              padding: '6px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#fef2f2';
                              e.target.style.borderColor = '#dc2626';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e5e7eb';
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                              <path d="M3 6H5H17M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{
                padding: '32px 20px',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', color: '#9ca3af' }}>
                  <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p style={{ fontSize: '14px', margin: '0' }}>No check batch allocations found</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowDeallocateSelectionModal(false);
                  setSelectedBatchForDeallocation(null);
                }}
                style={{ minWidth: '80px', padding: '8px 16px', fontSize: '14px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deallocation Confirmation Modal */}
      {showDeallocationConfirm && (
        <div 
          className="modal-overlay"
          onClick={handleDeallocationCancel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '450px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                flexShrink: 0
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#DC2626' }}>
                  <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                  Deallocate Check Batch
                </h3>
                <p style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                  Are you sure you want to deallocate this check batch?
                </p>
                {deallocationBatchInfo && (
                  <div style={{ 
                    padding: '10px 12px', 
                    backgroundColor: '#f9fafb', 
                    borderRadius: '6px', 
                    border: '1px solid #e5e7eb',
                    fontSize: '13px'
                  }}>
                    <div style={{ marginBottom: '6px', color: '#6b7280', lineHeight: '1.5' }}>
                      <strong style={{ color: '#374151', display: 'inline-block', minWidth: '120px' }}>Check Number:</strong> 
                      <span style={{ color: '#111827' }}>{deallocationBatchInfo.checkNumber || 'N/A'}</span>
                    </div>
                    <div style={{ marginBottom: '6px', color: '#6b7280', lineHeight: '1.5' }}>
                      <strong style={{ color: '#374151', display: 'inline-block', minWidth: '120px' }}>Batch Number:</strong> 
                      <span style={{ color: '#111827' }}>{deallocationBatchInfo.checkBatchNumber || 'N/A'}</span>
                    </div>
                    <div style={{ color: '#6b7280', lineHeight: '1.5' }}>
                      <strong style={{ color: '#374151', display: 'inline-block', minWidth: '120px' }}>Allocated Amount:</strong> 
                      <span style={{ color: '#111827', fontWeight: '600' }}>{formatCurrency(deallocationBatchInfo.allocatedAmount || 0)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button 
                onClick={handleDeallocationCancel}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeallocationConfirm}
                style={{ 
                  padding: '8px 16px',
                  backgroundColor: '#DC2626', 
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#B91C1C'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#DC2626'}
              >
                Deallocate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch View/Edit Modal */}
      {(viewingBatchId || batchModalMode === 'add') && (() => {
        const batch = viewingBatchId && viewingBatchId !== 'new' ? ditDrl?.batches?.find(b => b.batchId === viewingBatchId) : null;
        const isAddMode = batchModalMode === 'add';
        const isEditMode = batchModalMode === 'edit' && editingBatchId === viewingBatchId;
        const isViewMode = batchModalMode === 'view' && batch;
        
        return (
          <div 
            className="modal-overlay"
            onClick={() => {
              if (!isEditMode && !isAddMode) {
                setViewingBatchId(null);
                setBatchModalMode('view');
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div 
              className="modal-content batch-modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '700px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            >
              <div className="batch-modal-header">
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  {isAddMode ? 'Add Batch' : isEditMode ? 'Edit Batch' : 'Batch Details'}
                </h3>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelBatch();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div className="batch-modal-content">
                {/* Row 1: Batch Type | Batch Date */}
                <div className="batch-modal-row">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Type</label>
                    {(isEditMode || isAddMode) ? (
                      <select
                        value={batchFormData.batchType}
                        onChange={(e) => handleBatchFormChange('batchType', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        <option value="MANUAL">MANUAL</option>
                        <option value="AUTO">AUTO</option>
                      </select>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchType || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Date <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <USDateInput
                          name="batchDate"
                          value={batchFormData.batchDate}
                          onChange={(e) => handleBatchFormChange('batchDate', e.target.value)}
                          placeholder="MM/DD/YYYY"
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        />
                        {batchValidationErrors.batchDate && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchDate}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchDate ? formatDateUS(batch.batchDate) : 'N/A'}</div>
                    )}
                  </div>
                </div>

                {/* Row 2: Run Number | Batch Number */}
                <div className="batch-modal-row">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Run Number</label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="text"
                          value={batchFormData.batchType === 'MANUAL' ? '' : batchFormData.batchRunNumber}
                          onChange={(e) => handleBatchFormChange('batchRunNumber', e.target.value)}
                          disabled={batchFormData.batchType === 'MANUAL'}
                          readOnly={batchFormData.batchType === 'MANUAL'}
                          placeholder={batchFormData.batchType === 'MANUAL' ? '' : 'Enter run number'}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%',
                            backgroundColor: batchFormData.batchType === 'MANUAL' ? '#f3f4f6' : 'white',
                            cursor: batchFormData.batchType === 'MANUAL' ? 'not-allowed' : 'text'
                          }}
                        />
                        {batchValidationErrors.batchRunNumber && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchRunNumber}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchRunNumber || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Number <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="text"
                          value={batchFormData.batchNumber}
                          onChange={(e) => handleBatchFormChange('batchNumber', e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        />
                        {batchValidationErrors.batchNumber && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchNumber}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchNumber || 'N/A'}</div>
                    )}
                  </div>
                </div>

                {/* Row 3: EDM Number | Invoice Number */}
                <div className="batch-modal-row">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">EDM Number</label>
                    {(isEditMode || isAddMode) ? (
                      <input
                        type="text"
                        value={batchFormData.edmNumber}
                        onChange={(e) => handleBatchFormChange('edmNumber', e.target.value)}
                        disabled={batchFormData.batchType !== 'MANUAL'}
                        readOnly={batchFormData.batchType !== 'MANUAL'}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '100%',
                          backgroundColor: batchFormData.batchType !== 'MANUAL' ? '#f3f4f6' : 'white',
                          cursor: batchFormData.batchType !== 'MANUAL' ? 'not-allowed' : 'text'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.edmNumber || 'N/A'}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Invoice Number</label>
                    {(isEditMode || isAddMode) ? (
                      <input
                        type="text"
                        value={batchFormData.invoiceNumber}
                        onChange={(e) => handleBatchFormChange('invoiceNumber', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.invoiceNumber || 'N/A'}</div>
                    )}
                  </div>
                </div>

                {/* Row 4: Amount | Batch Source */}
                <div className="batch-modal-row">
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Amount <span style={{ color: '#dc2626' }}>*</span></label>
                    {(isEditMode || isAddMode) ? (
                      <>
                        <input
                          type="number"
                          value={batchFormData.batchAmount}
                          onChange={(e) => handleBatchFormChange('batchAmount', e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                          }}
                        />
                        {batchValidationErrors.batchAmount && (
                          <span style={{ color: '#dc2626', fontSize: '12px' }}>{batchValidationErrors.batchAmount}</span>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{formatCurrency(batch?.batchAmount || 0)}</div>
                    )}
                  </div>
                  <div className="batch-modal-field">
                    <label className="batch-modal-label">Batch Source</label>
                    {(isEditMode || isAddMode) ? (
                      <select
                        value={batchFormData.batchSource || 'ONSHORE'}
                        onChange={(e) => handleBatchFormChange('batchSource', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        <option value="ONSHORE">ONSHORE</option>
                        <option value="OFFSHORE">OFFSHORE</option>
                      </select>
                    ) : (
                      <div style={{ fontSize: '14px', color: '#111827' }}>{batch?.batchSource || 'N/A'}</div>
                    )}
                  </div>
                </div>

                {/* Row 6: Notes (full row) */}
                <div className="batch-modal-field batch-modal-field-full">
                  <label className="batch-modal-label">Notes</label>
                  {(isEditMode || isAddMode) ? (
                    <textarea
                      value={batchFormData.batchNotes}
                      onChange={(e) => handleBatchFormChange('batchNotes', e.target.value)}
                      rows="3"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                        width: '100%',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '14px', color: '#111827', whiteSpace: 'pre-wrap' }}>{batch?.batchNotes || 'N/A'}</div>
                  )}
                </div>

                {/* Row 7: Created At | Updated At */}
                {isViewMode && batch && (
                  <div className="batch-modal-row">
                    <div className="batch-modal-field">
                      <label className="batch-modal-label">Created</label>
                      <UserTimestamp
                        userId={batch.createdBy}
                        dateTime={batch.createdAt}
                        action="created"
                        getUserName={getUserName}
                        getUserById={getUserById}
                      />
                    </div>
                    <div className="batch-modal-field">
                      <label className="batch-modal-label">Updated</label>
                      <UserTimestamp
                        userId={batch.updatedBy}
                        dateTime={batch.updatedAt}
                        action="updated"
                        getUserName={getUserName}
                        getUserById={getUserById}
                      />
                    </div>
                  </div>
                )}
              </div>

              {(isEditMode || isAddMode) && (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button
                    className="btn-cancel"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCancelBatch();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isAddMode) {
                        await handleAddBatch();
                      } else {
                        await handleUpdateBatch();
                        setViewingBatchId(null);
                        setBatchModalMode('view');
                      }
                    }}
                    disabled={isAddMode ? isCreatingBatch : isUpdatingBatch}
                  >
                    {isAddMode 
                      ? (isCreatingBatch ? 'Creating...' : 'Add Batch')
                      : (isUpdatingBatch ? 'Updating...' : 'Save Changes')
                    }
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Check Batch Allocations Modal */}
      {showCheckBatchAllocationsModal && selectedBatchForCheckAllocations && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowCheckBatchAllocationsModal(false);
            setSelectedBatchForCheckAllocations(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Check Batch Allocations - DRL Batch {selectedBatchForCheckAllocations.batchNumber || 'N/A'}
              </h3>
              <button
                onClick={() => {
                  setShowCheckBatchAllocationsModal(false);
                  setSelectedBatchForCheckAllocations(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => e.target.style.color = '#111827'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {selectedBatchForCheckAllocations.allocations && selectedBatchForCheckAllocations.allocations.length > 0 ? (
              <div style={{ marginTop: '16px' }}>
                <div style={{ 
                  padding: '12px 16px', 
                  backgroundColor: '#f0fdf4', 
                  borderRadius: '6px', 
                  marginBottom: '16px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#166534', fontWeight: '500' }}>
                      Total Allocations: {selectedBatchForCheckAllocations.allocations.length}
                    </span>
                    <span style={{ fontSize: '16px', color: '#166534', fontWeight: '600' }}>
                      Total Amount: {formatCurrency(getTotalAllocatedAmount(selectedBatchForCheckAllocations))}
                    </span>
                  </div>
                </div>

                <div style={{ 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px'
                  }}>
                    <thead style={{ backgroundColor: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Check Number</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Batch Number</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Check Amount</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Allocated Amount</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: '12px' }}>Check Batch Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBatchForCheckAllocations.allocations.map((allocation, idx) => (
                        <tr
                          key={allocation.allocationId || idx}
                          style={{
                            backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb',
                            borderBottom: idx < selectedBatchForCheckAllocations.allocations.length - 1 ? '1px solid #e5e7eb' : 'none'
                          }}
                          onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = idx % 2 === 0 ? 'white' : '#f9fafb'}
                        >
                          <td style={{ padding: '10px 8px', color: '#111827' }}>
                            {allocation.checkNumber || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#111827' }}>
                            {allocation.checkBatchNumber ? (
                              <button
                                onClick={() => {
                                  setShowCheckBatchAllocationsModal(false);
                                  setSelectedBatchForCheckAllocations(null);
                                  navigate(`/checks/${allocation.checkId}?batchId=${allocation.checkBatchId}`);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#3b82f6',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0,
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  textAlign: 'left'
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                                onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
                              >
                                {allocation.checkBatchNumber}
                              </button>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                            {formatCurrency(allocation.checkTotalAmount || 0)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                            {formatCurrency(allocation.allocatedAmount || 0)}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                            {formatCurrency(allocation.checkBatchAmount || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{
                padding: '32px 20px',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 12px', color: '#9ca3af' }}>
                  <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p style={{ fontSize: '14px', margin: '0' }}>No check batch allocations found for this DRL batch</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowCheckBatchAllocationsModal(false);
                  setSelectedBatchForCheckAllocations(null);
                }}
                style={{ minWidth: '80px', padding: '8px 16px', fontSize: '14px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Drawer */}
      <ActivityDrawer
        isOpen={showActivityDrawer}
        onClose={() => setShowActivityDrawer(false)}
        checkId={ditDrl?.drlId}
        isDitDrl={true}
      />
    </div>
  );
};

export default DitDrlDetails;
